import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { SymbolSelectorPanel } from "@/components/SymbolSelectorPanel";
import { UserPipelineStatusBar } from "@/components/UserPipelineStatusBar";
import { ActiveSymbolScopeCard } from "@/components/user/ActiveSymbolScopeCard";
import { ScannerStatusHeader } from "@/components/user/ScannerStatusHeader";
import { UserPipelineStatusBar as UserPipelineStatusBarV2 } from "@/components/user/UserPipelineStatusBar";
import { WhyNoSignalCard } from "@/components/user/WhyNoSignalCard";
import { Button } from "@/components/ui/button";
import { apiClient, classifyApiError } from "@/lib/api";
import { UserIndicatorScreenerPage } from "@/pages/UserIndicatorScreenerPage";
import { DecisionCard } from "@/pages/user/components/DecisionCard";
import { ExplainabilityDrawer } from "@/pages/user/components/ExplainabilityDrawer";
import { V5BotTraderPanel } from "@/components/user/V5BotTraderPanel";

const AUTO_SCAN_INTERVAL_SECONDS = 60;
const PROFILE_INTERVAL_OPTIONS = [
  { value: 30, label: "30 saniye" },
  { value: 60, label: "1 dakika" },
  { value: 90, label: "1.5 dakika" },
  { value: 120, label: "2 dakika" },
];
const AUTO_INTERVAL_MINUTE_OPTIONS = [1, 3, 5];

const SIMPLE_SCANNER_V2 = true;
const PURE_LIVE_DISABLE_AUTOMATION = false;

const RUN_CHUNK_SIZE = 20;

const normalizeIntervalSeconds = (value) => {
  const allowed = new Set(PROFILE_INTERVAL_OPTIONS.map((option) => Number(option.value)));
  const parsed = Number(value || AUTO_SCAN_INTERVAL_SECONDS);
  if (allowed.has(parsed)) {
    return parsed;
  }
  if (!Number.isFinite(parsed)) {
    return AUTO_SCAN_INTERVAL_SECONDS;
  }
  if (parsed > 120) {
    return 120;
  }
  if (parsed < 30) {
    return 30;
  }
  return AUTO_SCAN_INTERVAL_SECONDS;
};

const normalizeSymbolsForRun = (symbols) => {
  const unique = Array.from(new Set((symbols || []).map((item) => String(item || "").trim().toUpperCase()).filter(Boolean)));
  return unique.filter((symbol) => symbol.endsWith("USDT") || symbol.endsWith("USDC"));
};

const normalizeEnginePreviewRows = (rows, runId) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  return rows
    .map((row, index) => {
      const candidate = String(row?.symbol || row?.candidate || "").trim().toUpperCase();
      const marketType = String(row?.market_type || "spot").trim().toLowerCase();
      if (!candidate) {
        return null;
      }

      const longScore = Number(row?.long_score || 0);
      const shortScore = Number(row?.short_score || 0);
      const providedScore = Number(row?.score || 0);
      const bestScore = Math.max(longScore, shortScore, providedScore, 0);
      const candidateTier = String(row?.candidate_tier || "").trim().toLowerCase() || (bestScore >= 130 ? "tier_a" : bestScore >= 100 ? "tier_b" : "tier_c");

      return {
        id: `${String(runId || "engine")}-${candidate}-${index}`,
        run_id: String(runId || ""),
        rank: Number(row?.rank || 0) > 0 ? Number(row.rank) : (index + 1),
        candidate,
        market_type: marketType,
        score: bestScore,
        candidate_tier: candidateTier,
        preview_status: String(row?.preview_status || "preview").trim().toLowerCase() || "preview",
        long_score: longScore,
        short_score: shortScore,
        classification: String(row?.classification || "neutral"),
      };
    })
    .filter(Boolean);
};

const normalizeSignalStatusForTable = (status) => {
  const raw = String(status || "").trim().toLowerCase();
  if (["approved", "submitted", "filled"].includes(raw)) {
    return "approved";
  }
  if (["rejected", "blocked", "non_tradeable", "expired", "canceled", "cancelled"].includes(raw)) {
    return "rejected";
  }
  return "pending";
};

const normalizeScannerSignalsRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }
  return rows.map((row, index) => ({
    id: String(row?.id || `${row?.signal_id || "signal"}-${index}`),
    symbol: String(row?.symbol || "").trim().toUpperCase(),
    signal: String(row?.signal || row?.signal_direction || "-").trim().toUpperCase() || "-",
    source: String(row?.source || "scanner_runtime").trim().toLowerCase() || "scanner_runtime",
    status: normalizeSignalStatusForTable(row?.status),
    raw_status: String(row?.status || "-").trim().toLowerCase() || "-",
    tradeable: Boolean(row?.tradeable ?? row?.execution_eligible),
    blocked_reason: Array.isArray(row?.blocked_reason_codes) && row.blocked_reason_codes.length > 0
      ? String(row.blocked_reason_codes[0] || "-")
      : "-",
    created_at: String(row?.signal_generated_at || row?.updated_at || row?.created_at || "").trim() || "-",
    market_type: String(row?.market_type || "spot").trim().toLowerCase(),
  })).filter((row) => Boolean(row.symbol));
};

const chunkSymbols = (symbols, size = RUN_CHUNK_SIZE) => {
  if (!Array.isArray(symbols) || symbols.length === 0) {
    return [];
  }
  const chunks = [];
  for (let i = 0; i < symbols.length; i += size) {
    chunks.push(symbols.slice(i, i + size));
  }
  return chunks;
};

const MINIMAL_FILTER_DEFAULTS = {
  rsi_min: "",
  rsi_max: "",
  volume_min: "",
  market_cap_min: "",
  timeframe: "1h",
};

const compactMinimalFilters = (filters) => Object.entries(filters || {}).reduce((acc, [key, value]) => {
  if (value === "" || value === null || typeof value === "undefined") {
    return acc;
  }
  if (key === "timeframe") {
    acc[key] = String(value).trim().toLowerCase();
    return acc;
  }
  const numeric = Number(value);
  if (!Number.isNaN(numeric)) {
    acc[key] = numeric;
  }
  return acc;
}, {});

const REQUEST_HEALTH_WINDOW_MS = 60_000;
const REQUEST_TREND_MAX_WINDOW_MS = 900_000;
const REQUEST_TREND_BUCKETS = 5;
const REQUEST_TREND_OPTIONS = [5, 15];
const SCANNER_ANOMALY_TOAST_PREF_KEY = "scanner-anomaly-toast-enabled-v1";
const SCANNER_ANOMALY_SOUND_PREF_KEY = "scanner-anomaly-sound-enabled-v1";
const SCANNER_AUTO_ENGINE_PREVIEW_PREF_KEY = "scanner-auto-engine-preview-enabled-v1";
const SCANNER_AUTO_SCANNER_RUN_PREF_KEY = "scanner-auto-scanner-run-enabled-v1";
const SCANNER_RUN_SETTINGS_KEY = "user-scanner-run-settings-v1";
const SCANNER_TOAST_DEDUPE_WINDOW_MS = 8000;

const isAnomalyEligibleEvent = (item) => {
  if (!item) {
    return false;
  }
  if (item.ok) {
    return true;
  }
  return String(item.errorClass || "").toLowerCase() === "infra_error";
};

const readScannerAlertPreference = (key, fallbackValue = true) => {
  if (typeof window === "undefined") {
    return fallbackValue;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === "1") {
      return true;
    }
    if (raw === "0") {
      return false;
    }
    return fallbackValue;
  } catch {
    return fallbackValue;
  }
};

const readScannerRunSettings = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(SCANNER_RUN_SETTINGS_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const buildTrendPoints = (events, windowMinutes = 5, nowTs = Date.now()) => {
  const normalizedWindowMinutes = REQUEST_TREND_OPTIONS.includes(Number(windowMinutes)) ? Number(windowMinutes) : 5;
  const totalWindowMs = normalizedWindowMinutes * 60_000;
  const bucketMs = Math.max(1, Math.floor(totalWindowMs / REQUEST_TREND_BUCKETS));
  const startTs = nowTs - totalWindowMs;
  const labelStep = Math.max(1, Math.round(normalizedWindowMinutes / REQUEST_TREND_BUCKETS));

  return Array.from({ length: REQUEST_TREND_BUCKETS }, (_, index) => {
    const bucketStart = startTs + (index * bucketMs);
    const bucketEnd = bucketStart + bucketMs;
    const bucketEventsRaw = events.filter((item) => item.timestamp >= bucketStart && item.timestamp < bucketEnd);
    const bucketEvents = bucketEventsRaw.filter((item) => isAnomalyEligibleEvent(item));
    const total = bucketEvents.length;
    const success = bucketEvents.filter((item) => item.ok).length;
    const failed = Math.max(total - success, 0);
    const successRatio = total > 0 ? success / total : 1;
    const minutesAgo = Math.max(1, normalizedWindowMinutes - (index * labelStep));
    const infraErrorCount = bucketEventsRaw.filter((item) => String(item.errorClass || "") === "infra_error").length;
    const authErrorCount = bucketEventsRaw.filter((item) => String(item.errorClass || "") === "auth_error").length;

    const endpointMap = bucketEvents.reduce((acc, item) => {
      const endpoint = String(item.endpoint || "unknown_endpoint");
      const current = acc.get(endpoint) || { endpoint, total: 0, success: 0, fail: 0 };
      current.total += 1;
      if (item.ok) {
        current.success += 1;
      } else {
        current.fail += 1;
      }
      acc.set(endpoint, current);
      return acc;
    }, new Map());

    const endpointBreakdown = Array.from(endpointMap.values())
      .map((row) => ({
        ...row,
        successRatio: row.total > 0 ? row.success / row.total : 1,
      }))
      .sort((a, b) => {
        if (b.fail !== a.fail) return b.fail - a.fail;
        if (b.total !== a.total) return b.total - a.total;
        return String(a.endpoint).localeCompare(String(b.endpoint));
      });

    return {
      key: `m${index}`,
      label: `${minutesAgo}m`,
      total,
      success,
      failed,
      successRatio,
      excluded: Math.max(bucketEventsRaw.length - bucketEvents.length, 0),
      infra_error_count: infraErrorCount,
      auth_error_count: authErrorCount,
      endpoint_breakdown: endpointBreakdown,
      most_impacted_endpoint: endpointBreakdown[0]?.endpoint || null,
    };
  });
};

const summarizeEndpointBreakdown = (events, { windowMs = 60_000, nowTs = Date.now() } = {}) => {
  const threshold = nowTs - windowMs;
  const scopedRaw = events.filter((item) => Number(item.timestamp || 0) >= threshold);
  const scoped = scopedRaw.filter((item) => isAnomalyEligibleEvent(item));
  const endpointMap = scoped.reduce((acc, item) => {
    const endpoint = String(item.endpoint || "unknown_endpoint");
    const current = acc.get(endpoint) || { endpoint, total: 0, success: 0, fail: 0 };
    current.total += 1;
    if (item.ok) {
      current.success += 1;
    } else {
      current.fail += 1;
    }
    acc.set(endpoint, current);
    return acc;
  }, new Map());

  const rows = Array.from(endpointMap.values())
    .map((row) => ({
      ...row,
      successRatio: row.total > 0 ? row.success / row.total : 1,
    }))
    .sort((a, b) => {
      if (b.fail !== a.fail) return b.fail - a.fail;
      if (b.total !== a.total) return b.total - a.total;
      return String(a.endpoint).localeCompare(String(b.endpoint));
    });

  const total = scoped.length;
  const success = scoped.filter((item) => item.ok).length;
  const failed = Math.max(total - success, 0);
  const successRatio = total > 0 ? success / total : 1;
  const infraErrorCount = scopedRaw.filter((item) => String(item.errorClass || "") === "infra_error").length;
  const authErrorCount = scopedRaw.filter((item) => String(item.errorClass || "") === "auth_error").length;

  return {
    rows,
    total,
    success,
    failed,
    successRatio,
    excluded: Math.max(scopedRaw.length - scoped.length, 0),
    infraErrorCount,
    authErrorCount,
    mostImpactedEndpoint: rows[0]?.endpoint || null,
  };
};

const toApiErrorMessage = (error, fallback = "İşlem başarısız") => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parsed = detail
      .map((item) => {
        if (!item) return "";
        if (typeof item === "string") return item;
        if (typeof item === "object") {
          return item.msg || item.message || item.type || "";
        }
        return String(item);
      })
      .filter(Boolean)
      .join(" | ");
    if (parsed) {
      return parsed;
    }
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.msg || detail.code || fallback;
  }
  const generic = error?.message || error?.response?.statusText;
  return generic || fallback;
};

const deriveRequestHealth = (events) => {
  const eligible = events.filter((event) => isAnomalyEligibleEvent(event));
  const total = eligible.length;
  const success = eligible.filter((event) => event.ok).length;
  const failed = Math.max(total - success, 0);
  const successRatio = total > 0 ? success / total : 1;

  if (total === 0) {
    return {
      total,
      success,
      failed,
      successRatio,
      health: "NO_DATA",
    };
  }
  if (successRatio >= 0.95) {
    return { total, success, failed, successRatio, health: "HEALTHY" };
  }
  if (successRatio >= 0.8) {
    return { total, success, failed, successRatio, health: "DEGRADED" };
  }
  return { total, success, failed, successRatio, health: "CRITICAL" };
};

export const LegacyUserScannerPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState("ASSISTED");
  const [marketType, setMarketType] = useState("spot");
  const [overview, setOverview] = useState(null);
  const [enginePreviewRows, setEnginePreviewRows] = useState([]);
  const [scannerSignals, setScannerSignals] = useState([]);
  const [scannerResultTab, setScannerResultTab] = useState("signals");
  const [isLoading, setIsLoading] = useState(true);
  const [symbolSource, setSymbolSource] = useState("crypto");
  const [symbolMode, setSymbolMode] = useState("manual_selection");
  const [symbolSelectorMarketType, setSymbolSelectorMarketType] = useState("spot");
  const [engineSymbolSelection, setEngineSymbolSelection] = useState({ spot: [], futures: [] });
  const [watchlistOnly, setWatchlistOnly] = useState(false);
  const [automationConfig, setAutomationConfig] = useState(null);
  const [automationProfiles, setAutomationProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState("");
  const [profileNameInput, setProfileNameInput] = useState("");
  const [profileIntervalInput, setProfileIntervalInput] = useState(AUTO_SCAN_INTERVAL_SECONDS);
  const [autoScanInterval, setAutoScanInterval] = useState(AUTO_SCAN_INTERVAL_SECONDS);
  const [autoRunAlerts, setAutoRunAlerts] = useState([]);
  const [runtimeSnapshot, setRuntimeSnapshot] = useState(null);
  const [liveReadiness, setLiveReadiness] = useState(null);
  const [scannerDailyReport, setScannerDailyReport] = useState(null);
  const [scannerStatusContract, setScannerStatusContract] = useState(null);
  const [scannerLoadDegraded, setScannerLoadDegraded] = useState(false);
  const [scannerLoadFailures, setScannerLoadFailures] = useState([]);
  const [decisionCards, setDecisionCards] = useState([]);
  const [selectedDecisionSymbol, setSelectedDecisionSymbol] = useState("");
  const [isExplainabilityDrawerOpen, setIsExplainabilityDrawerOpen] = useState(false);
  const [symbolExplainability, setSymbolExplainability] = useState(null);
  const [isExplainabilityLoading, setIsExplainabilityLoading] = useState(false);
  const [isSavingAutomation, setIsSavingAutomation] = useState(false);
  const [selectionSavedAt, setSelectionSavedAt] = useState(null);
  const [selectionHydrated, setSelectionHydrated] = useState(false);
  const [minimalFilters, setMinimalFilters] = useState(MINIMAL_FILTER_DEFAULTS);
  const [requestHealth, setRequestHealth] = useState({
    total: 0,
    success: 0,
    failed: 0,
    successRatio: 1,
    health: "NO_DATA",
    windowSeconds: 60,
    updatedAt: null,
  });
  const [selectedTrendWindowMinutes, setSelectedTrendWindowMinutes] = useState(5);
  const [scannerSection, setScannerSection] = useState(SIMPLE_SCANNER_V2 ? "results" : (searchParams.get("section") === "screener" ? "screener" : "results"));
  const [scannerTemplates, setScannerTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [lastRunEnvelope, setLastRunEnvelope] = useState(null);
  const [lastRunStartBotReport, setLastRunStartBotReport] = useState(null);
  const [requestTrend, setRequestTrend] = useState(() => buildTrendPoints([], 5));
  const [requestEndpointBreakdown, setRequestEndpointBreakdown] = useState({
    oneMinute: summarizeEndpointBreakdown([], { windowMs: 60_000 }),
    fiveMinute: summarizeEndpointBreakdown([], { windowMs: 300_000 }),
  });
  const [requestErrorBreakdown, setRequestErrorBreakdown] = useState({
    auth_error: 0,
    infra_error: 0,
    trade_blocker: 0,
  });
  const [scannerEngineConfig, setScannerEngineConfig] = useState({
    exchange: "binance",
    include_spot: true,
    include_futures: true,
    market_scope: {
      spot_mode: "all",
      futures_mode: "all",
    },
    signal_mode: "manual",
    auto_interval_minutes: 3,
    scan_limit: 80,
    batch_size: 25,
    top_n: 20,
    manual_symbols: [],
    manual_symbols_spot: [],
    manual_symbols_futures: [],
    weights: {
      trend: 10,
      volume: 50,
      momentum: 100,
      bollinger: 1,
      max_score: 161,
    },
  });
  const [scannerEngineUsdtSymbols, setScannerEngineUsdtSymbols] = useState({
    spot_symbols: [],
    futures_symbols: [],
  });
  const [scannerEngineRun, setScannerEngineRun] = useState({
    status: "empty",
    summary: {
      max_score: 161,
      candidate_count: 0,
      scored_count: 0,
      strong_long_count: 0,
      strong_short_count: 0,
    },
    results: [],
    top_results: [],
    errors: [],
  });
  const [scannerEngineBusy, setScannerEngineBusy] = useState(false);
  const [scannerFailureSummary, setScannerFailureSummary] = useState({
    auth_error: 0,
    infra_error: 0,
    trade_blocker: 0,
  });
  const [scannerRunBusy, setScannerRunBusy] = useState(false);
  const [anomalyToastEnabled, setAnomalyToastEnabled] = useState(() => readScannerAlertPreference(SCANNER_ANOMALY_TOAST_PREF_KEY, true));
  const [anomalySoundEnabled, setAnomalySoundEnabled] = useState(() => readScannerAlertPreference(SCANNER_ANOMALY_SOUND_PREF_KEY, true));
  const [autoEnginePreviewEnabled, setAutoEnginePreviewEnabled] = useState(() => readScannerAlertPreference(SCANNER_AUTO_ENGINE_PREVIEW_PREF_KEY, true));
  const [autoScannerRunEnabled, setAutoScannerRunEnabled] = useState(() => readScannerAlertPreference(SCANNER_AUTO_SCANNER_RUN_PREF_KEY, false));
  const [hoveredTrendPointKey, setHoveredTrendPointKey] = useState(null);
  const profileRunTrackerRef = useRef({});
  const minimalFiltersRef = useRef(MINIMAL_FILTER_DEFAULTS);
  const rotatingScanWindowRef = useRef(0);
  const requestWindowRef = useRef([]);
  const requestTrendRef = useRef([]);
  const selectedTrendWindowRef = useRef(5);
  const anomalyAlertArmedRef = useRef(false);
  const scannerToastTrackerRef = useRef(new Map());
  const initialFullLoadDoneRef = useRef(false);
  const loadRequestSequenceRef = useRef(0);
  const activeLoadAbortControllerRef = useRef(null);
  const activeLoadIsSilentRef = useRef(false);
  const isLoadingRef = useRef(true);

  const activeProfile = useMemo(() => {
    if (!automationProfiles.length) {
      return null;
    }
    return (
      automationProfiles.find((item) => item.id === activeProfileId)
      || automationProfiles.find((item) => item.is_active)
      || automationProfiles[0]
    );
  }, [activeProfileId, automationProfiles]);

  const activeAutomation = activeProfile || automationConfig;

  const selectedSpotSymbols = useMemo(
    () => Array.from(new Set((engineSymbolSelection?.spot || []).map((item) => String(item || "").trim().toUpperCase()).filter(Boolean))),
    [engineSymbolSelection?.spot],
  );

  const selectedFuturesSymbols = useMemo(
    () => Array.from(new Set((engineSymbolSelection?.futures || []).map((item) => String(item || "").trim().toUpperCase()).filter(Boolean))),
    [engineSymbolSelection?.futures],
  );

  const effectiveSelectedSymbols = useMemo(
    () => Array.from(new Set([...selectedSpotSymbols, ...selectedFuturesSymbols])),
    [selectedFuturesSymbols, selectedSpotSymbols],
  );

  const selectedSymbolsForRun = useMemo(() => {
    const normalizedMarket = String(marketType || "spot").toLowerCase();
    if (normalizedMarket === "futures") {
      return selectedFuturesSymbols;
    }
    if (normalizedMarket === "both") {
      return effectiveSelectedSymbols;
    }
    return selectedSpotSymbols;
  }, [effectiveSelectedSymbols, marketType, selectedFuturesSymbols, selectedSpotSymbols]);

  const selectedSymbols = effectiveSelectedSymbols;

  const activeMinimalFilterChips = useMemo(() => {
    const mapping = {
      rsi_min: "RSI min",
      rsi_max: "RSI max",
      volume_min: "Volume min",
      market_cap_min: "Market Cap min",
      timeframe: "Timeframe",
    };
    return Object.entries(minimalFilters)
      .filter(([key, value]) => key === "timeframe" ? Boolean(value) : value !== "")
      .map(([key, value]) => ({ key, label: `${mapping[key]}: ${value}` }));
  }, [minimalFilters]);

  const activeModeLabel = String(overview?.mode || mode || "ASSISTED").toUpperCase();
  const selectedAutoIntervalMinutes = AUTO_INTERVAL_MINUTE_OPTIONS.includes(Number(scannerEngineConfig?.auto_interval_minutes))
    ? Number(scannerEngineConfig?.auto_interval_minutes)
    : 3;
  const scannerRunType = autoScannerRunEnabled ? "OTOMATİK SCANNER RUN" : "MANUEL SCANNER RUN";
  const scannerRunTypeDetail = `Auto Scanner: ${autoScannerRunEnabled ? "Açık" : "Kapalı"} · Auto Engine Preview: ${autoEnginePreviewEnabled ? "Açık" : "Kapalı"} · ${selectedAutoIntervalMinutes} dk`;
  const signalReviewModeLabel = String(mode || "ASSISTED").toUpperCase();
  const scannerAutomationModeLabel = autoScannerRunEnabled ? `AUTO_SCANNER_RUN_${selectedAutoIntervalMinutes}M` : "MANUAL_SCANNER_RUN";
  const executionDispatchModeLabel = signalReviewModeLabel === "AUTO" ? "AUTO_DISPATCH_ENABLED" : "REVIEW_REQUIRED";
  const executionPathLabel =
    activeModeLabel === "AUTO"
      ? "BOT_AUTO_ACTIVE"
      : "BOT_AUTO_PENDING";

  const requestHealthBadgeClass =
    requestHealth.health === "HEALTHY"
      ? "border-emerald-700 bg-emerald-950/40 text-emerald-300"
      : requestHealth.health === "DEGRADED"
        ? "border-amber-700 bg-amber-950/40 text-amber-300"
        : requestHealth.health === "CRITICAL"
          ? "border-red-700 bg-red-950/40 text-red-300"
          : "border-slate-700 bg-slate-950 text-slate-300";

  const scannerEngineNextRunAt = useMemo(() => {
    const baseRaw = scannerEngineRun?.generated_at;
    if (!baseRaw) {
      return null;
    }
    const baseDate = new Date(baseRaw);
    if (Number.isNaN(baseDate.getTime())) {
      return null;
    }
    const minutes = selectedAutoIntervalMinutes;
    return new Date(baseDate.getTime() + (minutes * 60 * 1000)).toISOString();
  }, [
    scannerEngineRun?.generated_at,
    selectedAutoIntervalMinutes,
  ]);

  const scannerRuntimeLastRunAt = useMemo(() => {
    const topLevel = String(scannerStatusContract?.latest_scanner_run_at || "").trim();
    if (topLevel) {
      return topLevel;
    }
    const preferredMarket = String(scannerStatusContract?.preferred_market || marketType || "spot").toLowerCase();
    const marketContract = scannerStatusContract?.market_contracts?.[preferredMarket] || null;
    const marketLastRunAt = String(marketContract?.latest_scanner_run_at || "").trim();
    if (marketLastRunAt) {
      return marketLastRunAt;
    }
    return null;
  }, [marketType, scannerStatusContract]);

  const scannerRuntimeNextRunAt = useMemo(() => {
    if (!autoScannerRunEnabled) {
      return null;
    }
    if ((selectedSymbolsForRun || []).length === 0) {
      return "BLOCKED_NO_SYMBOL";
    }
    if (!scannerRuntimeLastRunAt) {
      return null;
    }
    const baseDate = new Date(scannerRuntimeLastRunAt);
    if (Number.isNaN(baseDate.getTime())) {
      return null;
    }
    return new Date(baseDate.getTime() + (selectedAutoIntervalMinutes * 60 * 1000)).toISOString();
  }, [autoScannerRunEnabled, scannerRuntimeLastRunAt, selectedAutoIntervalMinutes, selectedSymbolsForRun]);

  const persistedSelectionCount = useMemo(() => {
    const activeMarket = String(marketType || "spot").toLowerCase();
    const marketContract = scannerStatusContract?.market_contracts?.[activeMarket] || {};
    const contractCount = Number(marketContract?.selected_symbol_count);
    if (Number.isFinite(contractCount)) {
      return contractCount;
    }
    return Number((selectedSymbolsForRun || []).length || 0);
  }, [marketType, scannerStatusContract?.market_contracts, selectedSymbolsForRun]);

  const selectionEmptyFromPersisted = persistedSelectionCount <= 0;

  const autoScannerBlocked = useMemo(
    () => autoScannerRunEnabled && selectionEmptyFromPersisted,
    [autoScannerRunEnabled, selectionEmptyFromPersisted],
  );

  const scannerReasonCode = useMemo(() => {
    if (selectionEmptyFromPersisted) {
      return "selection_empty";
    }
    return String(scannerStatusContract?.scanner_result_reason || "").trim().toLowerCase();
  }, [selectionEmptyFromPersisted, scannerStatusContract?.scanner_result_reason]);

  const scannerReasonText = useMemo(() => {
    const mapping = {
      selection_empty: "Seçili market için sembol yok.",
      trade_blocker_active: "Trade blocker aktif; signal materialization veya dispatch kısıtlanabilir.",
      no_actionable_candidates: "Scanner çalıştı ancak actionable aday çıkmadı.",
      materialization_blocked: "Aday var ancak materialization aşamasında bloklandı.",
      preview_only: "Sadece preview var, gerçek scanner sonucu yok.",
      scanner_not_run_yet: "Henüz gerçek scanner run tetiklenmedi.",
      signals_materialized: "Signal materialization başarılı.",
    };
    return mapping[scannerReasonCode] || (scannerReasonCode ? scannerReasonCode : "reason_unavailable");
  }, [scannerReasonCode]);

  const previewMetaSource = useMemo(() => {
    if (String(lastRunEnvelope?.source || "") === "cache" || String(lastRunEnvelope?.status || "") === "completed_cached") {
      return "cached";
    }
    return "live";
  }, [lastRunEnvelope?.source, lastRunEnvelope?.status]);

  const scannerEngineVersionLabel = useMemo(() => {
    const value =
      scannerEngineRun?.config?.engine_version
      || scannerEngineConfig?.engine_version
      || "v1";
    return String(value || "v1").toLowerCase();
  }, [scannerEngineConfig?.engine_version, scannerEngineRun?.config?.engine_version]);

  const scannerEngineLastRunId = String(scannerEngineRun?.run_id || overview?.latest_run_id || "-");

  const spotUsdtUniverse = useMemo(
    () => (Array.isArray(scannerEngineUsdtSymbols?.spot_symbols) ? scannerEngineUsdtSymbols.spot_symbols : []),
    [scannerEngineUsdtSymbols],
  );
  const futuresUsdtUniverse = useMemo(
    () => (Array.isArray(scannerEngineUsdtSymbols?.futures_symbols) ? scannerEngineUsdtSymbols.futures_symbols : []),
    [scannerEngineUsdtSymbols],
  );

  const scannerDecisionModeSummary = useMemo(() => {
    return "ENGINE_PREVIEW_ONLY";
  }, []);

  const scannerFeatureTelemetry = useMemo(() => {
    const rows = Array.isArray(enginePreviewRows) ? enginePreviewRows : [];
    const samples = rows
      .map((row) => Number(row?.payload?.indicator_snapshot_age_sec))
      .filter((value) => Number.isFinite(value));
    const staleCount = rows.filter((row) => {
      const age = Number(row?.payload?.indicator_snapshot_age_sec);
      const threshold = Number(row?.payload?.freshness_sla_seconds || 0);
      if (!Number.isFinite(age) || !Number.isFinite(threshold) || threshold <= 0) {
        return false;
      }
      return age > threshold;
    }).length;

    const averageAge = samples.length > 0
      ? Number((samples.reduce((total, value) => total + value, 0) / samples.length).toFixed(1))
      : null;

    return {
      sampleCount: samples.length,
      averageAge,
      staleCount,
      freshnessRatio: rows.length > 0 ? Number((((rows.length - staleCount) / rows.length) * 100).toFixed(1)) : 100,
    };
  }, [enginePreviewRows]);

  const scannerSignalTradeAlignment = useMemo(() => {
    const rows = Array.isArray(scannerSignals) ? scannerSignals : [];
    let longCount = 0;
    let shortCount = 0;
    let noTradeCount = 0;
    let allocationBlockedCount = 0;
    let tradeableCount = 0;

    rows.forEach((row) => {
      const finalDecision = String(row?.signal || "").toUpperCase();
      if (finalDecision === "LONG") {
        longCount += 1;
      } else if (finalDecision === "SHORT") {
        shortCount += 1;
      } else {
        noTradeCount += 1;
      }

      if (String(row?.status || "").toLowerCase() === "rejected") {
        allocationBlockedCount += 1;
      }

      if (Boolean(row?.tradeable)) {
        tradeableCount += 1;
      }
    });

    return {
      total: rows.length,
      longCount,
      shortCount,
      noTradeCount,
      allocationBlockedCount,
      tradeableCount,
    };
  }, [scannerSignals]);

  const scannerRunSourceLabel = useMemo(() => {
    const source = String(lastRunEnvelope?.source || "").toLowerCase();
    if (source === "cache") {
      return "CACHE_FIRST";
    }
    if (source === "live") {
      return "LIVE_REFRESH";
    }
    return "BACKGROUND_REFRESH";
  }, [lastRunEnvelope?.source]);

  const engineResultBadge = useMemo(() => {
    const runStatus = String(scannerEngineRun?.status || "").toLowerCase();
    const envelopeStatus = String(lastRunEnvelope?.status || "").toLowerCase();
    const origin = String(scannerEngineRun?.result_origin || "").toLowerCase();

    if (envelopeStatus === "failed" || runStatus === "failed") {
      return {
        label: "failed",
        className: "border-red-700 bg-red-950/40 text-red-300",
      };
    }
    if (envelopeStatus === "running" || runStatus === "running" || origin.includes("running")) {
      return {
        label: "running",
        className: "border-amber-700 bg-amber-950/40 text-amber-300",
      };
    }
    if (envelopeStatus === "completed_cached" || runStatus === "completed_cached" || origin.includes("cached")) {
      return {
        label: "cached",
        className: "border-blue-700 bg-blue-950/40 text-blue-300",
      };
    }
    if (String(scannerEngineRun?.run_id || "").trim()) {
      return {
        label: "fresh",
        className: "border-emerald-700 bg-emerald-950/40 text-emerald-300",
      };
    }
    return {
      label: "idle",
      className: "border-slate-700 bg-slate-950 text-slate-300",
    };
  }, [lastRunEnvelope?.status, scannerEngineRun?.result_origin, scannerEngineRun?.run_id, scannerEngineRun?.status]);

  const scannerLatencyBreakdown = useMemo(() => {
    const summary = scannerEngineRun?.summary || {};
    const queue = Number(summary.queue_latency_ms);
    const feature = Number(summary.feature_calc_ms || summary.feature_latency_ms);
    const score = Number(summary.score_merge_ms || summary.scoring_latency_ms);
    const total = Number(summary.total_latency_ms || summary.scan_duration_ms || summary.cycle_duration_ms);

    const asLabel = (value) => (Number.isFinite(value) && value >= 0 ? `${value.toFixed(0)}ms` : "-");

    return {
      queueLabel: asLabel(queue),
      featureLabel: asLabel(feature),
      scoreLabel: asLabel(score),
      totalLabel: asLabel(total),
    };
  }, [scannerEngineRun?.summary]);

  const requestTrendPolylinePoints = useMemo(() => {
    const width = 160;
    const height = 32;
    if (!requestTrend.length) {
      return [];
    }
    return requestTrend.map((point, index) => {
      const x = requestTrend.length === 1 ? 0 : (index / (requestTrend.length - 1)) * width;
      const normalized = Math.max(0, Math.min(1, Number(point.successRatio || 0)));
      const y = height - (normalized * height);
      return {
        ...point,
        x,
        y,
        fail: Math.max(Number(point.total || 0) - Number(point.success || 0), 0),
      };
    });
  }, [requestTrend]);

  const requestTrendPolylinePath = useMemo(() => {
    if (!requestTrendPolylinePoints.length) {
      return "";
    }
    return requestTrendPolylinePoints.map((point) => {
      return `${point.x},${point.y}`;
    }).join(" ");
  }, [requestTrendPolylinePoints]);

  const latestTrendPoint = requestTrendPolylinePoints[requestTrendPolylinePoints.length - 1] || null;
  const hoveredTrendPoint = useMemo(
    () => requestTrendPolylinePoints.find((point) => point.key === hoveredTrendPointKey) || null,
    [hoveredTrendPointKey, requestTrendPolylinePoints],
  );
  const trendDetailPoint = hoveredTrendPoint || latestTrendPoint;

  const latestFailRatio = requestHealth.total > 0
    ? Number((requestHealth.failed / requestHealth.total).toFixed(4))
    : 0;
  const hasAnomaly = Boolean(requestHealth.total > 0 && latestFailRatio > 0.1);

  const formatDateLabel = (value) => {
    if (!value) {
      return "-";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "-";
    }
    return parsed.toLocaleString("tr-TR");
  };

  const formatEndpointLabel = (value) => String(value || "unknown_endpoint").replaceAll("_", " ");
  const endpointFailRatio = (row) => {
    const total = Number(row?.total || 0);
    if (total <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(1, Number(row?.fail || 0) / total));
  };

  useEffect(() => {
    minimalFiltersRef.current = minimalFilters;
  }, [minimalFilters]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(SCANNER_ANOMALY_TOAST_PREF_KEY, anomalyToastEnabled ? "1" : "0");
      window.localStorage.setItem(SCANNER_ANOMALY_SOUND_PREF_KEY, anomalySoundEnabled ? "1" : "0");
      window.localStorage.setItem(SCANNER_AUTO_ENGINE_PREVIEW_PREF_KEY, autoEnginePreviewEnabled ? "1" : "0");
      window.localStorage.setItem(SCANNER_AUTO_SCANNER_RUN_PREF_KEY, autoScannerRunEnabled ? "1" : "0");
    } catch {
      // noop
    }
  }, [anomalySoundEnabled, anomalyToastEnabled, autoEnginePreviewEnabled, autoScannerRunEnabled]);

  const emitScannerToast = useCallback((level, key, message) => {
    if (!message) {
      return;
    }
    const now = Date.now();
    const previous = scannerToastTrackerRef.current.get(key) || 0;
    if (now - previous < SCANNER_TOAST_DEDUPE_WINDOW_MS) {
      return;
    }
    scannerToastTrackerRef.current.set(key, now);
    const notifier = toast[level] || toast.info;
    notifier(message, { id: key });
  }, []);

  const updateRequestHealthWindow = useCallback((settledResponses = []) => {
    const now = Date.now();
    const retained = (requestWindowRef.current || []).filter((item) => now - item.timestamp <= REQUEST_HEALTH_WINDOW_MS);
    const incoming = Array.isArray(settledResponses)
      ? settledResponses.map((item) => {
        const ok = item?.status === "fulfilled";
        const reason = ok ? null : item?.reason;
        return {
          timestamp: now,
          ok,
          endpoint: String(item?.endpoint || item?.meta?.endpoint || "unknown_endpoint"),
          statusCode: ok ? Number(item?.value?.status || 200) : Number(reason?.response?.status || 0),
          errorCode: ok ? "" : String(reason?.response?.data?.code || "").toUpperCase(),
          errorClass: ok ? "success" : classifyApiError(reason),
        };
      })
      : [];
    const merged = [...retained, ...incoming];
    requestWindowRef.current = merged;

    const retainedTrend = (requestTrendRef.current || []).filter((item) => now - item.timestamp <= REQUEST_TREND_MAX_WINDOW_MS);
    const mergedTrend = [...retainedTrend, ...incoming];
    requestTrendRef.current = mergedTrend;

    const metrics = deriveRequestHealth(merged);
    setRequestHealth({
      ...metrics,
      windowSeconds: 60,
      updatedAt: new Date(now).toISOString(),
    });
    setRequestTrend(buildTrendPoints(mergedTrend, selectedTrendWindowRef.current, now));
    setRequestEndpointBreakdown({
      oneMinute: summarizeEndpointBreakdown(mergedTrend, { windowMs: 60_000, nowTs: now }),
      fiveMinute: summarizeEndpointBreakdown(mergedTrend, { windowMs: 300_000, nowTs: now }),
    });

    const latestFailures = merged.filter((item) => !item.ok);
    setRequestErrorBreakdown({
      auth_error: latestFailures.filter((item) => String(item.errorClass || "") === "auth_error").length,
      infra_error: latestFailures.filter((item) => String(item.errorClass || "") === "infra_error").length,
      trade_blocker: 0,
    });
  }, []);

  const playAnomalyAlertBeep = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    const AudioContextRef = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextRef) {
      return;
    }
    try {
      const ctx = new AudioContextRef();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 860;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.09, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.24);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.25);
      setTimeout(() => {
        ctx.close().catch(() => {});
      }, 350);
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    const now = Date.now();
    selectedTrendWindowRef.current = selectedTrendWindowMinutes;
    setRequestTrend(buildTrendPoints(requestTrendRef.current || [], selectedTrendWindowMinutes, now));
  }, [selectedTrendWindowMinutes]);

  useEffect(() => {
    if (!hasAnomaly) {
      anomalyAlertArmedRef.current = false;
      return;
    }
    if (anomalyAlertArmedRef.current) {
      return;
    }
    anomalyAlertArmedRef.current = true;
    if (anomalyToastEnabled) {
      emitScannerToast(
        "error",
        "scanner-trade-anomaly-alert",
        `Anomaly tespit edildi: son 1 dakikada fail oranı %${(latestFailRatio * 100).toFixed(1)}`,
      );
    }
    if (anomalySoundEnabled) {
      playAnomalyAlertBeep();
    }
    apiClient.post("/user/scanner/runtime/anomaly-event", {
      source: "scanner_ui",
      fail_ratio: latestFailRatio,
      total_requests: requestHealth.total,
      failed_requests: requestHealth.failed,
      success_requests: requestHealth.success,
      trend_window_minutes: selectedTrendWindowMinutes,
      trend_points: requestTrendPolylinePoints.map((point) => ({
        label: point.label,
        total: point.total,
        success: point.success,
        fail: point.fail,
        success_ratio: Number((point.successRatio || 0).toFixed(4)),
      })),
    }).catch(() => {
      // audit write best effort
    });
  }, [
    anomalySoundEnabled,
    anomalyToastEnabled,
    emitScannerToast,
    hasAnomaly,
    latestFailRatio,
    playAnomalyAlertBeep,
    requestHealth.failed,
    requestHealth.success,
    requestHealth.total,
    requestTrendPolylinePoints,
    selectedTrendWindowMinutes,
  ]);

  const loadSymbolExplainability = async (symbol) => {
    if (!symbol) {
      setSymbolExplainability(null);
      return;
    }
    setIsExplainabilityLoading(true);
    try {
      const { data } = await apiClient.get(`/user/explainability/${encodeURIComponent(symbol)}`);
      setSymbolExplainability(data || null);
    } catch (error) {
      setSymbolExplainability(null);
      toast.error(toApiErrorMessage(error, "Explainability yüklenemedi"));
    } finally {
      setIsExplainabilityLoading(false);
    }
  };

  const saveAutomationConfig = async ({ autoEnabled, withToast = true } = {}) => {
    const payload = {
      auto_enabled: Boolean(autoEnabled),
      interval_seconds: normalizeIntervalSeconds(autoScanInterval),
      max_results: Number(automationConfig?.max_results || 40),
      symbol_source: symbolSource,
      symbol_selection_mode: "manual_selection",
      selected_symbols: selectedSymbols,
    };
    const response = await apiClient.put("/user/scanner/automation", payload, { timeout: 15000 });
    const saved = response?.data || payload;
    setAutomationConfig(saved);
    if (withToast) {
      toast.success(saved.auto_enabled ? "Automation aktif" : "Automation kapatıldı");
    }
    return saved;
  };

  const syncAutoRunNotifications = (profiles, { notify = false } = {}) => {
    const tracker = profileRunTrackerRef.current || {};
    const nextTracker = { ...tracker };

    for (const profile of profiles || []) {
      const lastRunId = String(profile?.last_run_id || "");
      const previousRunId = String(tracker?.[profile.id] || "");
      if (notify && lastRunId && previousRunId && lastRunId !== previousRunId && profile?.last_run_status === "success") {
        const newSignalCount = Number(profile?.last_actionable_count || 0);
        if (newSignalCount > 0) {
          toast.success(`Yeni sinyal var: ${profile.name} (${newSignalCount})`);
          setAutoRunAlerts((prev) => [
            {
              id: `${profile.id}-${lastRunId}`,
              profile_name: profile.name,
              signal_count: newSignalCount,
              run_at: profile.last_run_at,
            },
            ...prev,
          ].slice(0, 10));
        }
      }
      if (lastRunId) {
        nextTracker[profile.id] = lastRunId;
      }
    }

    profileRunTrackerRef.current = nextTracker;
  };

  const saveActiveProfile = async ({ autoEnabled, withToast = true } = {}) => {
    return saveAutomationConfig({ autoEnabled, withToast });
  };

  const createAutomationProfile = async () => {
    const payload = {
      name: String(profileNameInput || "Yeni Profil").trim() || "Yeni Profil",
      auto_enabled: true,
      is_active: true,
      interval_seconds: normalizeIntervalSeconds(profileIntervalInput),
      max_results: Number(automationConfig?.max_results || 40),
      symbol_source: symbolSource,
      symbol_selection_mode: "manual_selection",
      selected_symbols: selectedSymbols,
    };
    const response = await apiClient.post("/user/scanner/automation-profiles", payload, { timeout: 15000 });
    setAutomationProfiles((prev) => [response?.data, ...(prev || []).filter((item) => item.id !== response?.data?.id)]);
    setActiveProfileId(response?.data?.id || "");
    toast.success("Automation profili oluşturuldu.");
  };

  const activateAutomationProfile = async (profileId) => {
    const response = await apiClient.post(`/user/scanner/automation-profiles/${profileId}/activate`, {}, { timeout: 15000 });
    setAutomationProfiles((prev) => (prev || []).map((item) => ({ ...item, is_active: item.id === response?.data?.id })));
    setActiveProfileId(response?.data?.id || profileId);
    toast.success("Automation profili aktif edildi.");
  };

  const deleteAutomationProfile = async (profileId) => {
    await apiClient.delete(`/user/scanner/automation-profiles/${profileId}`, { timeout: 15000 });
    setAutomationProfiles((prev) => (prev || []).filter((item) => item.id !== profileId));
    if (activeProfileId === profileId) {
      setActiveProfileId("");
    }
    toast.success("Automation profili silindi.");
  };

  const load = useCallback(async ({ hydrateSelection = false, silent = false, notifyAutoRuns = false } = {}) => {
    if (activeLoadAbortControllerRef.current) {
      const previousWasSilent = Boolean(activeLoadIsSilentRef.current);
      if (silent && !previousWasSilent) {
        return;
      }
      activeLoadAbortControllerRef.current.abort();
    }

    const currentRequestSequence = loadRequestSequenceRef.current + 1;
    loadRequestSequenceRef.current = currentRequestSequence;

    const abortController = new AbortController();
    activeLoadAbortControllerRef.current = abortController;
    activeLoadIsSilentRef.current = Boolean(silent);

    if (!silent) {
      setIsLoading(true);
    }

    const requestConfig = (timeout) => ({ timeout, signal: abortController.signal });

    try {
      const requestDescriptors = SIMPLE_SCANNER_V2
        ? [
            { key: "scanner_overview", request: apiClient.get("/user/scanner", requestConfig(12000)) },
            { key: "strategy_templates", request: apiClient.get("/strategy-templates", requestConfig(15000)) },
            { key: "scanner_signals", request: apiClient.get("/user/signals", { params: { limit: 120 }, ...requestConfig(12000) }) },
            { key: "scanner_status_contract", request: apiClient.get("/user/scanner/status-contract", { params: { market_type: marketType }, ...requestConfig(12000) }) },
            { key: "scanner_engine_config", request: apiClient.get("/user/scanner-engine/config", requestConfig(12000)) },
            { key: "scanner_engine_usdt_symbols", request: apiClient.get("/user/scanner-engine/usdt-symbols", requestConfig(12000)) },
            { key: "scanner_engine_last_run", request: apiClient.get("/user/scanner-engine/last-run", requestConfig(15000)) },
            { key: "scanner_engine_preview", request: apiClient.get("/user/scanner-engine/preview", requestConfig(15000)) },
          ]
        : [
            { key: "scanner_overview", request: apiClient.get("/user/scanner", requestConfig(12000)) },
            { key: "strategy_templates", request: apiClient.get("/strategy-templates", requestConfig(15000)) },
            { key: "decision_cards", request: apiClient.get("/user/decision-cards", { params: { limit: 60 }, ...requestConfig(12000) }) },
            { key: "scanner_signals", request: apiClient.get("/user/signals", { params: { limit: 120 }, ...requestConfig(12000) }) },
            { key: "scanner_status_contract", request: apiClient.get("/user/scanner/status-contract", { params: { market_type: marketType }, ...requestConfig(12000) }) },
            { key: "runtime_snapshot", request: apiClient.get("/user/scanner/runtime/snapshot", requestConfig(12000)) },
            { key: "live_readiness", request: apiClient.get("/user/scanner/runtime/live-readiness", { params: { window: "24h" }, ...requestConfig(12000) }) },
            { key: "daily_report", request: apiClient.get("/user/scanner/runtime/daily-report", { params: { window: "24h" }, ...requestConfig(12000) }) },
            { key: "scanner_engine_config", request: apiClient.get("/user/scanner-engine/config", requestConfig(12000)) },
            { key: "scanner_engine_usdt_symbols", request: apiClient.get("/user/scanner-engine/usdt-symbols", requestConfig(12000)) },
            { key: "scanner_engine_last_run", request: apiClient.get("/user/scanner-engine/last-run", requestConfig(15000)) },
            { key: "scanner_engine_preview", request: apiClient.get("/user/scanner-engine/preview", requestConfig(15000)) },
          ];
      const responses = await Promise.allSettled(requestDescriptors.map((entry) => entry.request));

      if (currentRequestSequence !== loadRequestSequenceRef.current || abortController.signal.aborted) {
        return;
      }

      const responsesWithEndpointMeta = responses.map((entry, index) => ({
        ...entry,
        endpoint: requestDescriptors[index]?.key,
      }));

      const nonCanceledResponses = responsesWithEndpointMeta.filter((entry) => !(
        entry.status === "rejected"
        && String(entry?.reason?.code || "").toUpperCase() === "ERR_CANCELED"
      ));

      updateRequestHealthWindow(nonCanceledResponses);

      const failedIndexes = responses
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => (
          item.status === "rejected"
          && String(item?.reason?.code || "").toUpperCase() !== "ERR_CANCELED"
        ))
        .map(({ index }) => index);

      const failedEntries = failedIndexes
        .map((index) => {
          const key = requestDescriptors[index]?.key;
          const reason = responses[index]?.reason;
          if (!key) {
            return null;
          }
          return {
            key,
            errorClass: classifyApiError(reason),
            status: Number(reason?.response?.status || 0),
          };
        })
        .filter(Boolean);

      const failedKeys = failedEntries.map((entry) => entry.key);
      const failureSummary = {
        auth_error: failedEntries.filter((entry) => entry.errorClass === "auth_error").length,
        infra_error: failedEntries.filter((entry) => entry.errorClass === "infra_error").length,
        trade_blocker: 0,
      };
      const infraErrorKeys = failedEntries
        .filter((entry) => entry.errorClass === "infra_error")
        .map((entry) => `${entry.key}${entry.status ? `(${entry.status})` : ""}`);
      const authErrorKeys = failedEntries
        .filter((entry) => entry.errorClass === "auth_error")
        .map((entry) => `${entry.key}${entry.status ? `(${entry.status})` : ""}`);

      const criticalTradeBlockerKeys = (SIMPLE_SCANNER_V2
        ? failedEntries.filter((entry) => entry.key !== "strategy_templates")
        : failedEntries
      )
        .filter(() => false)
        .map((entry) => entry.key);

      setScannerLoadFailures(failedKeys);
      setScannerFailureSummary(failureSummary);
      setScannerLoadDegraded(failedEntries.length > 0);

      if (!silent && failedEntries.length > 0) {
        if (false) {
          emitScannerToast(
            "error",
            "scanner-load-trade-blocker",
            `Scanner kısmi yüklendi (trade blocker): ${criticalTradeBlockerKeys.join(", ")}`,
          );
        }
        if (failureSummary.infra_error > 0) {
          emitScannerToast(
            "warning",
            "scanner-load-infra-error",
            `Altyapı sorunu: ${infraErrorKeys.slice(0, 5).join(", ") || `${failureSummary.infra_error} endpoint`} (anomaly hariç).`,
          );
        }
        if (failureSummary.auth_error > 0) {
          emitScannerToast(
            "warning",
            "scanner-load-auth-error",
            `Auth sorunu: ${authErrorKeys.slice(0, 5).join(", ") || `${failureSummary.auth_error} endpoint`} (anomaly hariç).`,
          );
        }
      }

      const byKey = requestDescriptors.reduce((acc, descriptor, index) => {
        const result = responses[index];
        acc[descriptor.key] = result?.status === "fulfilled" ? result.value : null;
        return acc;
      }, {});

      const overviewRes = byKey.scanner_overview;
      const templatesRes = byKey.strategy_templates;
      const cardsRes = byKey.decision_cards;
      const scannerSignalsRes = byKey.scanner_signals;
      const scannerStatusContractRes = byKey.scanner_status_contract;
      const runtimeRes = byKey.runtime_snapshot;
      const readinessRes = byKey.live_readiness;
      const dailyRes = byKey.daily_report;
      const scannerEngineConfigRes = byKey.scanner_engine_config;
      const scannerEngineUsdtSymbolsRes = byKey.scanner_engine_usdt_symbols;
      const scannerEngineRunRes = byKey.scanner_engine_last_run;
      const scannerEnginePreviewRes = byKey.scanner_engine_preview;

      setMode((prev) => prev || "ASSISTED");
      setOverview((prev) => overviewRes?.data || prev || null);
      const scannerSignalRows = Array.isArray(scannerSignalsRes?.data)
        ? scannerSignalsRes.data
        : (Array.isArray(scannerSignalsRes?.data?.items) ? scannerSignalsRes.data.items : []);
      const activeTemplates = (templatesRes?.data || []).filter((item) => item.is_active);
      setScannerTemplates(activeTemplates);
      setSelectedTemplateId((prev) => prev || activeTemplates[0]?.id || "");
      const automation = null;
      const profiles = [];
      setAutomationConfig(null);
      setAutomationProfiles([]);
      const cards = cardsRes?.data?.items || [];
      const statusContractPayload = scannerStatusContractRes?.data || null;
      setRuntimeSnapshot((prev) => runtimeRes?.data || prev || null);
      setLiveReadiness((prev) => readinessRes?.data || prev || null);
      setScannerDailyReport((prev) => dailyRes?.data || prev || null);
      setScannerStatusContract(statusContractPayload);
      const nextConfig = scannerEngineConfigRes?.data || {};
      const usdtUniverse = scannerEngineUsdtSymbolsRes?.data || {};
      const spotUniverse = Array.isArray(usdtUniverse?.spot_symbols) ? usdtUniverse.spot_symbols : [];
      const futuresUniverse = Array.isArray(usdtUniverse?.futures_symbols) ? usdtUniverse.futures_symbols : [];

      setScannerEngineUsdtSymbols({
        spot_symbols: spotUniverse,
        futures_symbols: futuresUniverse,
      });

      setScannerEngineConfig((prev) => ({ ...prev, ...nextConfig }));

      const loadedSpotSelection = Array.isArray(nextConfig?.manual_symbols_spot)
        ? nextConfig.manual_symbols_spot
        : (Array.isArray(nextConfig?.manual_symbols) ? nextConfig.manual_symbols : []);
      const loadedFuturesSelection = Array.isArray(nextConfig?.manual_symbols_futures)
        ? nextConfig.manual_symbols_futures
        : (Array.isArray(nextConfig?.manual_symbols) ? nextConfig.manual_symbols : []);
      const shouldHydrateEngineSelections = Boolean((hydrateSelection || !selectionHydrated) && !silent);

      setEngineSymbolSelection((prev) => {
        if (!shouldHydrateEngineSelections) {
          return prev;
        }
        const nextSpot = loadedSpotSelection.length > 0
          ? loadedSpotSelection.filter((symbol) => spotUniverse.includes(symbol))
          : [];
        const nextFutures = loadedFuturesSelection.length > 0
          ? loadedFuturesSelection.filter((symbol) => futuresUniverse.includes(symbol))
          : [];
        return {
          spot: Array.from(new Set(nextSpot.map((item) => String(item || "").trim().toUpperCase()).filter(Boolean))),
          futures: Array.from(new Set(nextFutures.map((item) => String(item || "").trim().toUpperCase()).filter(Boolean))),
        };
      });

      const scannerEngineRunPayload = scannerEngineRunRes?.data || {
        status: "empty",
        summary: { max_score: 161, candidate_count: 0, scored_count: 0, strong_long_count: 0, strong_short_count: 0 },
        results: [],
        top_results: [],
        errors: [],
      };
      setScannerEngineRun(scannerEngineRunPayload);
      const previewItems = Array.isArray(scannerEnginePreviewRes?.data?.items)
        ? scannerEnginePreviewRes.data.items
        : [];
      const normalizedEngineRows = normalizeEnginePreviewRows(
        previewItems.length > 0 ? previewItems : scannerEngineRunPayload?.results,
        scannerEngineRunPayload?.run_id,
      );
      setEnginePreviewRows(normalizedEngineRows);
      setScannerSignals(normalizeScannerSignalsRows(scannerSignalRows));
      setDecisionCards(cards);

      if (cards.length > 0) {
        const selectedSymbol = selectedDecisionSymbol || cards[0].symbol;
        setSelectedDecisionSymbol(selectedSymbol);
        loadSymbolExplainability(selectedSymbol);
      } else {
        setSelectedDecisionSymbol("");
        setSymbolExplainability(null);
      }
      syncAutoRunNotifications([], { notify: false });

      if (hydrateSelection) {
        const localRunSettings = readScannerRunSettings();
        setActiveProfileId("");
        setSymbolSource("crypto");
        setSymbolMode("manual_selection");
        setSelectionSavedAt(nextConfig?.updated_at || null);
        if (localRunSettings) {
          if (localRunSettings.mode) {
            setMode(String(localRunSettings.mode).toUpperCase());
          }
          if (localRunSettings.marketType) {
            setMarketType(String(localRunSettings.marketType).toLowerCase());
          }
          if (typeof localRunSettings.watchlistOnly === "boolean") {
            setWatchlistOnly(Boolean(localRunSettings.watchlistOnly));
          }
        }
        setWatchlistOnly((loadedSpotSelection.length + loadedFuturesSelection.length) > 0);
        if (SIMPLE_SCANNER_V2) {
          setSymbolMode("manual_selection");
        }
        setSelectionHydrated(true);
      }
    } finally {
      const isCurrentRequest = currentRequestSequence === loadRequestSequenceRef.current;
      if (isCurrentRequest && activeLoadAbortControllerRef.current === abortController) {
        activeLoadAbortControllerRef.current = null;
        activeLoadIsSilentRef.current = false;
      }
      if (!silent && isCurrentRequest) {
        setIsLoading(false);
      }
    }
  }, [
    emitScannerToast,
    marketType,
    selectionHydrated,
    selectedDecisionSymbol,
    updateRequestHealthWindow,
  ]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    return () => {
      if (activeLoadAbortControllerRef.current) {
        activeLoadAbortControllerRef.current.abort();
        activeLoadAbortControllerRef.current = null;
      }
      activeLoadIsSilentRef.current = false;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      updateRequestHealthWindow([]);
    }, 5000);
    return () => clearInterval(timer);
  }, [updateRequestHealthWindow]);

  useEffect(() => {
    if (initialFullLoadDoneRef.current) {
      return;
    }
    initialFullLoadDoneRef.current = true;
    load({ hydrateSelection: true });
  }, [load]);

  useEffect(() => {
    if (!selectionHydrated || typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(
        SCANNER_RUN_SETTINGS_KEY,
        JSON.stringify({
          mode: String(mode || "ASSISTED").toUpperCase(),
          marketType: String(marketType || "spot").toLowerCase(),
          watchlistOnly: Boolean(watchlistOnly),
        }),
      );
    } catch {
      // noop
    }
  }, [selectionHydrated, mode, marketType, watchlistOnly]);

  useEffect(() => {
    if (!selectionHydrated) {
      return;
    }
    load({ silent: true, notifyAutoRuns: false });
  }, [selectionHydrated, minimalFilters, load]);

  useEffect(() => {
    const intervalMs = SIMPLE_SCANNER_V2 ? 30_000 : 10_000;
    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) {
        return;
      }
      if (isLoadingRef.current) {
        return;
      }
      load({ silent: true, notifyAutoRuns: true });
    }, intervalMs);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    // Pure Live: automation endpointleri kaldırıldı.
  }, []);

  useEffect(() => {
    if (!watchlistOnly) {
      return;
    }
    if (symbolMode !== "manual_selection") {
      setSymbolMode("manual_selection");
    }
  }, [watchlistOnly, symbolMode]);

  useEffect(() => {
    if (!selectionHydrated) {
      return;
    }
    setSelectionSavedAt((prev) => prev || scannerEngineConfig?.updated_at || null);
  }, [selectionHydrated, scannerEngineConfig?.updated_at]);

  const executeScannerRunWithChunks = useCallback(async ({ runMode, maxResults, effectiveMode, selectedForRun, targetMarketType = marketType, reason = "user_scanner_run" }) => {
    const runScannerAsync = async (requestPayload) => {
      const normalizedMarketType = String(requestPayload?.market_type || "spot").toLowerCase();
      const endpoint = normalizedMarketType === "both" ? "/user/scanner/run-async-both" : "/user/scanner/run-async";
      const { data: queued } = await apiClient.post(endpoint, requestPayload, { timeout: 12000 });
      const jobId = String(queued?.job_id || "").trim();
      if (!jobId) {
        throw new Error("scanner_async_job_id_missing");
      }

      const deadline = Date.now() + 180_000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const { data: job } = await apiClient.get(`/user/scanner/run-async/${jobId}`, { timeout: 10000 });
        const status = String(job?.status || "").toLowerCase();
        if (status === "completed") {
          return job?.result || {};
        }
        if (status === "failed") {
          throw new Error(String(job?.error || "scanner_run_async_failed"));
        }
      }

      throw new Error("scanner_run_async_timeout");
    };

    const symbols = normalizeSymbolsForRun(selectedForRun || []);
    const isChunked = SIMPLE_SCANNER_V2 && targetMarketType !== "both" && effectiveMode === "manual_selection" && symbols.length > RUN_CHUNK_SIZE;

    if (!isChunked) {
      const data = await runScannerAsync({
        mode: runMode,
        max_results: maxResults,
        symbol_source: symbolSource,
        market_type: targetMarketType,
        symbol_selection_mode: effectiveMode,
        selected_symbols: symbols,
        strategy_template_id: selectedTemplateId || null,
        reason,
      });
      return data;
    }

    const chunks = chunkSymbols(symbols, RUN_CHUNK_SIZE);
    const windowIndex = rotatingScanWindowRef.current % chunks.length;
    const selectedWindow = chunks[windowIndex] || chunks[0] || [];
    rotatingScanWindowRef.current = (windowIndex + 1) % chunks.length;

    const data = await runScannerAsync({
      mode: runMode,
      max_results: Math.min(40, Math.max(10, maxResults)),
      symbol_source: symbolSource,
      market_type: targetMarketType,
      symbol_selection_mode: "manual_selection",
      selected_symbols: selectedWindow,
      strategy_template_id: selectedTemplateId || null,
      reason,
    });

    const warnings = Array.isArray(data?.warnings) ? [...data.warnings.map((item) => String(item || ""))] : [];
    warnings.push(`rotating_window:${windowIndex + 1}/${chunks.length}`);

    return {
      run_id: data?.run_id || null,
      mode: runMode,
      market_type: targetMarketType,
      symbol_selection_mode: "manual_selection",
      selected_symbols: symbols,
      scanned_window_symbols: selectedWindow,
      result_count: Number(data?.result_count || 0),
      actionable_count: Number(data?.actionable_count || 0),
      non_tradeable_count: Number(data?.non_tradeable_count || 0),
      queued_count: Number(data?.queued_count || 0),
      pending_total: Number(data?.pending_total || 0),
      warnings,
      chunked: true,
      chunk_count: chunks.length,
      chunk_success_count: 1,
      chunk_index: windowIndex + 1,
    };
  }, [marketType, selectedTemplateId, symbolSource]);

  const exportScannerDailyReport = async (format) => {
    try {
      const isCsv = format === "csv";
      const response = await apiClient.get("/user/scanner/runtime/daily-report/export", {
        params: { format, window: "24h" },
        responseType: isCsv ? "blob" : "json",
      });

      const blob = isCsv
        ? new Blob([response.data], { type: "text/csv" })
        : new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = isCsv
        ? `scanner_daily_report_${scannerDailyReport?.date || "latest"}.csv`
        : `scanner_daily_report_${scannerDailyReport?.date || "latest"}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Scanner günlük rapor ${format.toUpperCase()} hazır`);
    } catch (error) {
      toast.error(toApiErrorMessage(error, "Scanner rapor export başarısız"));
    }
  };

  const saveScannerEngineConfig = async () => {
    setScannerEngineBusy(true);
    try {
      const normalizedSpotSymbols = selectedSpotSymbols
        .map((item) => String(item || "").trim().toUpperCase())
        .filter(Boolean);
      const normalizedFuturesSymbols = selectedFuturesSymbols
        .map((item) => String(item || "").trim().toUpperCase())
        .filter(Boolean);
      const manualSymbols = Array.from(new Set([...normalizedSpotSymbols, ...normalizedFuturesSymbols]));

      const includeSpot = Boolean(scannerEngineConfig.include_spot);
      const includeFutures = Boolean(scannerEngineConfig.include_futures);
      const spotMode = includeSpot ? "manual" : "all";
      const futuresMode = includeFutures ? "manual" : "all";
      const inferredScanLimit = Number(scannerEngineConfig.scan_limit || 80);

      const payload = {
        exchange: "binance",
        include_spot: includeSpot,
        include_futures: includeFutures,
        market_scope: {
          spot_mode: spotMode,
          futures_mode: futuresMode,
        },
        signal_mode: String(scannerEngineConfig.signal_mode || "manual").toLowerCase(),
        auto_interval_minutes: [1, 3, 5].includes(Number(scannerEngineConfig.auto_interval_minutes))
          ? Number(scannerEngineConfig.auto_interval_minutes)
          : 3,
        scan_limit: inferredScanLimit,
        batch_size: Number(scannerEngineConfig.batch_size || 25),
        top_n: Number(scannerEngineConfig.top_n || 20),
        manual_symbols: manualSymbols,
        manual_symbols_spot: normalizedSpotSymbols,
        manual_symbols_futures: normalizedFuturesSymbols,
        trend_weight: Number(scannerEngineConfig?.weights?.trend ?? 10),
        volume_weight: Number(scannerEngineConfig?.weights?.volume ?? 50),
        momentum_weight: Number(scannerEngineConfig?.weights?.momentum ?? 100),
        bollinger_weight: Number(scannerEngineConfig?.weights?.bollinger ?? 1),
        reason: "user_scanner_engine_config_save",
      };
      const { data } = await apiClient.post("/user/scanner-engine/config/save", payload);
      setScannerEngineConfig((prev) => ({ ...prev, ...(data?.config || payload) }));
      toast.success("Scanner Engine ayarları kaydedildi");
      await load({ silent: true });
    } catch (error) {
      toast.error(toApiErrorMessage(error, "Scanner Engine ayarı kaydedilemedi"));
    } finally {
      setScannerEngineBusy(false);
    }
  };

  const runScannerEngine = useCallback(async ({ silentSuccess = false, silentError = false, reason = "user_scanner_engine_run" } = {}) => {
    if (scannerEngineBusy) {
      return;
    }
    setScannerEngineBusy(true);
    try {
      const normalizedSpotSymbols = selectedSpotSymbols
        .map((item) => String(item || "").trim().toUpperCase())
        .filter(Boolean);
      const normalizedFuturesSymbols = selectedFuturesSymbols
        .map((item) => String(item || "").trim().toUpperCase())
        .filter(Boolean);
      const includeSpot = Boolean(scannerEngineConfig.include_spot);
      const includeFutures = Boolean(scannerEngineConfig.include_futures);

      if (includeSpot && normalizedSpotSymbols.length === 0) {
        throw new Error("engine_manual_symbols_spot_required");
      }
      if (includeFutures && normalizedFuturesSymbols.length === 0) {
        throw new Error("engine_manual_symbols_futures_required");
      }

      const manualSymbols = Array.from(new Set([...normalizedSpotSymbols, ...normalizedFuturesSymbols]));
      const { data: queued } = await apiClient.post("/user/scanner-engine/run-async", {
        force_refresh: false,
        reason,
        manual_symbols: manualSymbols,
        manual_symbols_spot: normalizedSpotSymbols,
        manual_symbols_futures: normalizedFuturesSymbols,
      }, { timeout: 12000 });

      if (queued?.status === "completed_cached") {
        setLastRunEnvelope({
          source: "cache",
          status: "completed_cached",
          run_id: queued?.run_id || scannerEngineRun?.run_id || null,
          at: new Date().toISOString(),
        });
        await load({ silent: true });
        if (!silentSuccess) {
          toast.success(`Önceki sonuç kullanıldı: ${queued?.summary?.scored_count || 0} sembol skorlandı`);
        }
        return;
      }

      const jobId = String(queued?.job_id || "").trim();
      if (!jobId) {
        throw new Error("scanner_engine_async_job_id_missing");
      }

      const deadline = Date.now() + 240_000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const { data: statusData } = await apiClient.get(`/user/scanner-engine/run-async/${jobId}`, { timeout: 10000 });
        const status = String(statusData?.status || "").toLowerCase();
        if (status === "completed") {
          setLastRunEnvelope({
            source: "live",
            status: "completed",
            run_id: statusData?.run_id || jobId,
            at: new Date().toISOString(),
          });
          await load({ silent: true });
          if (!silentSuccess) {
            toast.success(`Scanner tamamlandı: ${statusData?.summary?.scored_count || 0} sembol skorlandı`);
          }
          return;
        }
        if (status === "failed") {
          setLastRunEnvelope({
            source: "live",
            status: "failed",
            run_id: statusData?.run_id || jobId,
            at: new Date().toISOString(),
          });
          throw new Error(String(statusData?.error || "scanner_engine_run_async_failed"));
        }
      }

      setLastRunEnvelope((prev) => ({
        source: prev?.source || "live",
        status: "running",
        run_id: queued?.job_id || prev?.run_id || null,
        at: new Date().toISOString(),
      }));
      if (!silentError) {
        toast.info("Tarama devam ediyor. Sonuçlar arka planda tamamlanınca yansıyacak.");
      }
      return;
    } catch (error) {
      setLastRunEnvelope((prev) => ({
        source: prev?.source || "live",
        status: "failed",
        run_id: prev?.run_id || null,
        at: new Date().toISOString(),
      }));
      if (!silentError) {
        toast.error(toApiErrorMessage(error, "Scanner Engine run başarısız"));
      }
    } finally {
      setScannerEngineBusy(false);
    }
  }, [
    load,
    scannerEngineBusy,
    scannerEngineConfig.include_futures,
    scannerEngineConfig.include_spot,
    scannerEngineRun?.run_id,
    selectedFuturesSymbols,
    selectedSpotSymbols,
  ]);

  const runScannerRuntime = useCallback(async ({ silentSuccess = false, silentError = false, reason = "user_scanner_run" } = {}) => {
    if (scannerRunBusy) {
      return;
    }

    const scopedSymbols = normalizeSymbolsForRun(selectedSymbolsForRun || []);
    if (scopedSymbols.length === 0) {
      setLastRunEnvelope((prev) => ({
        source: "scanner_async",
        status: "blocked",
        run_id: prev?.run_id || null,
        at: new Date().toISOString(),
        reason: "selection_empty",
      }));
      if (!silentError) {
        toast.error("İşlem için en az bir geçerli USDT/USDC market seçmelisiniz.");
      }
      return;
    }

    const runMode = String(mode || "ASSISTED").toUpperCase();
    const normalizedMarketType = String(marketType || "spot").toLowerCase();
    const effectiveMode = scopedSymbols.length > 0 ? "manual_selection" : "all_market_symbols";
    const configuredMaxResults = Number(scannerEngineConfig?.scan_limit || (SIMPLE_SCANNER_V2 ? 120 : 25));
    const maxResults = Math.max(configuredMaxResults, scopedSymbols.length);

    setScannerRunBusy(true);
    try {
      const result = await executeScannerRunWithChunks({
        runMode,
        maxResults,
        effectiveMode,
        selectedForRun: scopedSymbols,
        targetMarketType: normalizedMarketType,
        reason,
      });

      setLastRunEnvelope({
        source: "scanner_async",
        status: "completed",
        run_id: result?.run_id || null,
        at: new Date().toISOString(),
        reason,
      });
      setScannerResultTab("signals");
      await load({ silent: true });
      if (!silentSuccess) {
        const actionable = Number(result?.actionable_count || 0);
        if (actionable > 0) {
          toast.success(`Scanner run tamamlandı: ${actionable} actionable sinyal`);
        } else {
          // P14-G UI transparency: alım açılmadıysa nedenini özetle göster
          const breakdown = result?.block_reason_breakdown || {};
          const topReason = Object.entries(breakdown).sort((a, b) => b[1] - a[1])[0];
          const reasonText = topReason
            ? ` En sık blok: ${topReason[0]} (${topReason[1]} sembol)`
            : " Hiçbir sembolden uygun sinyal gelmedi.";
          toast.info(
            `Scanner çalıştı, ancak uygun sinyal bulunamadı.${reasonText}`,
            { duration: 6000 },
          );
        }
      }
    } catch (error) {
      setLastRunEnvelope((prev) => ({
        source: "scanner_async",
        status: "failed",
        run_id: prev?.run_id || null,
        at: new Date().toISOString(),
        reason,
      }));
      if (!silentError) {
        toast.error(toApiErrorMessage(error, "Scanner run başarısız"));
      }
    } finally {
      setScannerRunBusy(false);
    }
  }, [
    executeScannerRunWithChunks,
    load,
    marketType,
    mode,
    scannerEngineConfig?.scan_limit,
    scannerRunBusy,
    selectedSymbolsForRun,
  ]);

  useEffect(() => {
    if (!selectionHydrated) {
      return;
    }
    if (!autoEnginePreviewEnabled) {
      return;
    }
    const intervalMinutes = AUTO_INTERVAL_MINUTE_OPTIONS.includes(Number(scannerEngineConfig?.auto_interval_minutes))
      ? Number(scannerEngineConfig?.auto_interval_minutes)
      : 3;
    const intervalMs = intervalMinutes * 60_000;

    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) {
        return;
      }
      runScannerEngine({
        silentSuccess: true,
        silentError: true,
        reason: "user_scanner_engine_auto_interval_tick",
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [autoEnginePreviewEnabled, runScannerEngine, scannerEngineConfig?.auto_interval_minutes, selectionHydrated]);

  useEffect(() => {
    if (!selectionHydrated) {
      return;
    }
    if (!autoScannerRunEnabled) {
      return;
    }

    const intervalMinutes = AUTO_INTERVAL_MINUTE_OPTIONS.includes(Number(scannerEngineConfig?.auto_interval_minutes))
      ? Number(scannerEngineConfig?.auto_interval_minutes)
      : 3;
    const intervalMs = intervalMinutes * 60_000;

    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) {
        return;
      }
      runScannerRuntime({
        silentSuccess: true,
        silentError: true,
        reason: "user_scanner_auto_interval_tick",
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [autoScannerRunEnabled, runScannerRuntime, scannerEngineConfig?.auto_interval_minutes, selectionHydrated]);

  useEffect(() => {
    if (SIMPLE_SCANNER_V2) {
      setScannerSection("results");
      return;
    }
    const nextSection = searchParams.get("section") === "screener" ? "screener" : "results";
    setScannerSection(nextSection);
  }, [searchParams]);

  if (isLoading) {
    return (
      <section className="space-y-4" data-testid="user-scanner-page">
        <header className="rounded-2xl border border-indigo-900/50 bg-slate-900 p-4" data-testid="user-scanner-header">
          <h2 className="text-4xl font-black uppercase tracking-tight text-indigo-200" data-testid="user-scanner-title">User Scanner</h2>
          <p className="mt-2 text-sm text-slate-400" data-testid="user-scanner-subtitle">Tarama ekranı yükleniyor...</p>
        </header>
        <LoadingSkeleton rows={6} testId="user-scanner-loading-skeleton" />
      </section>
    );
  }

  const switchScannerSection = (nextSection) => {
    setScannerSection(nextSection);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("section", nextSection === "screener" ? "screener" : "results");
    setSearchParams(nextParams, { replace: true });
  };

  const onSelectDecisionCard = async (symbol) => {
    setSelectedDecisionSymbol(symbol);
    await loadSymbolExplainability(symbol);
    setIsExplainabilityDrawerOpen(true);
  };

  const openSymbolDetail = (symbol) => {
    navigate(`/user/symbol/${encodeURIComponent(symbol)}`);
  };

  return (
    <section className="grid grid-cols-12 gap-4" data-testid="user-scanner-page">
      <header className="col-span-12 border border-slate-800 bg-slate-900 p-4" data-testid="user-scanner-header">
        <h2 className="text-4xl font-black uppercase tracking-tight" data-testid="user-scanner-title">Scanner</h2>
        <p className="mt-2 text-sm text-slate-400" data-testid="user-scanner-description">Responsive scanner + compact table + mobile card yapısı.</p>
      </header>

      {!SIMPLE_SCANNER_V2 && <section className="order-2 col-span-12 rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-scanner-section-toggle-panel">
        <div className="flex flex-wrap items-center gap-2" data-testid="user-scanner-section-toggle-group">
          <Button type="button" variant={scannerSection === "results" ? "default" : "outline"} onClick={() => switchScannerSection("results")} data-testid="user-scanner-section-results-button">Scan Results</Button>
          <Button type="button" variant={scannerSection === "screener" ? "default" : "outline"} onClick={() => switchScannerSection("screener")} data-testid="user-scanner-section-screener-button">Indicator Screener</Button>
        </div>
      </section>}

      {!SIMPLE_SCANNER_V2 && scannerSection === "screener" ? (
        <div className="col-span-12" data-testid="user-scanner-embedded-screener-wrapper">
          <UserIndicatorScreenerPage embedded />
        </div>
      ) : (
      <>

      {!SIMPLE_SCANNER_V2 && <section className="col-span-12 rounded border border-slate-800 bg-slate-900 p-3" data-testid="user-scanner-request-health-mini-indicator">
        <div className="flex flex-wrap items-center gap-3" data-testid="user-scanner-request-health-row">
          <p className="text-xs uppercase tracking-widest text-slate-400" data-testid="user-scanner-request-health-title">Scanner Request Health</p>
          <div className="flex items-center gap-1" data-testid="user-scanner-request-health-trend-window-toggle">
            {REQUEST_TREND_OPTIONS.map((minutes) => (
              <Button
                key={`trend-window-${minutes}`}
                type="button"
                size="sm"
                variant={selectedTrendWindowMinutes === minutes ? "default" : "outline"}
                className="h-7 px-2 text-xs"
                data-testid={`user-scanner-request-health-trend-window-${minutes}m-button`}
                onClick={() => setSelectedTrendWindowMinutes(minutes)}
              >
                {minutes}m
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-1" data-testid="user-scanner-request-health-alert-toggle-group">
            <Button
              type="button"
              size="sm"
              variant={anomalyToastEnabled ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              data-testid="user-scanner-request-health-anomaly-toast-toggle"
              onClick={() => setAnomalyToastEnabled((prev) => !prev)}
            >
              Toast {anomalyToastEnabled ? "Açık" : "Kapalı"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={anomalySoundEnabled ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              data-testid="user-scanner-request-health-anomaly-sound-toggle"
              onClick={() => setAnomalySoundEnabled((prev) => !prev)}
            >
              Ses {anomalySoundEnabled ? "Açık" : "Kapalı"}
            </Button>
          </div>
          <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${requestHealthBadgeClass}`} data-testid="user-scanner-request-health-badge">
            {requestHealth.health}
          </span>
          <p className="text-xs text-slate-300" data-testid="user-scanner-request-health-window">
            Son {requestHealth.windowSeconds}s request: <span className="font-semibold">{requestHealth.total}</span>
          </p>
          <p className="text-xs text-slate-300" data-testid="user-scanner-request-health-success-fail">
            ok/fail: <span className="font-semibold">{requestHealth.success}/{requestHealth.failed}</span>
          </p>
          <p className="text-xs text-slate-300" data-testid="user-scanner-request-health-success-ratio">
            başarı oranı: <span className="font-semibold">{(requestHealth.successRatio * 100).toFixed(1)}%</span>
          </p>
          <div className="ml-auto flex items-center gap-2" data-testid="user-scanner-request-health-trend-wrapper">
            <svg width="160" height="32" viewBox="0 0 160 32" className="rounded border border-slate-700 bg-slate-950" data-testid="user-scanner-request-health-trend-sparkline">
              <polyline
                fill="none"
                stroke="#22d3ee"
                strokeWidth="2"
                points={requestTrendPolylinePath}
                data-testid="user-scanner-request-health-trend-polyline"
              />
              {requestTrendPolylinePoints.map((point) => (
                <circle
                  key={point.key}
                  cx={point.x}
                  cy={point.y}
                  r="2.4"
                  fill="#22d3ee"
                  data-testid={`user-scanner-request-health-trend-point-${point.key}`}
                  onMouseEnter={() => setHoveredTrendPointKey(point.key)}
                  onMouseLeave={() => setHoveredTrendPointKey(null)}
                >
                  <title>{`${point.label}: ok=${point.success}, fail=${point.fail}, success=${(point.successRatio * 100).toFixed(1)}%`}</title>
                </circle>
              ))}
            </svg>
            <div className="flex gap-1" data-testid="user-scanner-request-health-trend-labels">
              {requestTrendPolylinePoints.map((point) => (
                <span
                  key={point.key}
                  className="text-[10px] text-slate-400"
                  data-testid={`user-scanner-request-health-trend-label-${point.key}`}
                  onMouseEnter={() => setHoveredTrendPointKey(point.key)}
                  onMouseLeave={() => setHoveredTrendPointKey(null)}
                >
                  {point.label}
                </span>
              ))}
            </div>
          </div>
        </div>
        {trendDetailPoint && (
          <div className="mt-2 rounded border border-slate-700 bg-slate-950 p-2 text-xs text-slate-300" data-testid="user-scanner-request-health-trend-detail-card">
            <p data-testid="user-scanner-request-health-trend-detail-title">
              Hover detay: <span className="font-semibold text-cyan-300">{trendDetailPoint.label}</span>
            </p>
            <p data-testid="user-scanner-request-health-trend-detail-values">
              total={trendDetailPoint.total}, ok={trendDetailPoint.success}, fail={trendDetailPoint.fail}, success={(trendDetailPoint.successRatio * 100).toFixed(1)}%
            </p>
            <p data-testid="user-scanner-request-health-trend-detail-most-impacted-endpoint">
              most impacted endpoint: <span className="font-semibold text-amber-300">{formatEndpointLabel(trendDetailPoint.most_impacted_endpoint || "n/a")}</span>
            </p>
            <div className="mt-1 grid gap-1" data-testid="user-scanner-request-health-trend-detail-endpoint-list">
              {(trendDetailPoint.endpoint_breakdown || []).slice(0, 3).map((endpointRow, index) => (
                <div key={`${trendDetailPoint.key}-endpoint-${endpointRow.endpoint}`} className="space-y-1" data-testid={`user-scanner-request-health-trend-detail-endpoint-item-${index}`}>
                  <p>
                    {formatEndpointLabel(endpointRow.endpoint)} → ok/fail {endpointRow.success}/{endpointRow.fail}
                  </p>
                  <div className="h-1.5 w-full overflow-hidden rounded bg-slate-800" data-testid={`user-scanner-request-health-trend-detail-endpoint-sparkbar-${index}`}>
                    <div
                      className="h-full bg-rose-400"
                      style={{ width: `${(endpointFailRatio(endpointRow) * 100).toFixed(1)}%` }}
                      data-testid={`user-scanner-request-health-trend-detail-endpoint-sparkbar-fill-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <p
          className={`mt-2 text-xs ${hasAnomaly ? "text-red-300" : "text-emerald-300"}`}
          data-testid="user-scanner-request-health-anomaly-flag"
        >
          {hasAnomaly
            ? `⚠️ Execution anomaly: son 1 dakikada fail oranı %${(latestFailRatio * 100).toFixed(1)} (>10)`
            : `✅ Execution anomaly yok: son 1 dakikada fail oranı %${(latestFailRatio * 100).toFixed(1)} (≤10)`}
        </p>
        <section className="mt-2 grid gap-2 md:grid-cols-2" data-testid="user-scanner-request-health-endpoint-breakdown-card">
          <div className="rounded border border-slate-700 bg-slate-950 p-2 text-xs text-slate-300" data-testid="user-scanner-request-health-endpoint-breakdown-1m-card">
            <p className="font-semibold text-cyan-300" data-testid="user-scanner-request-health-endpoint-breakdown-1m-title">Son 1m Endpoint Katkı</p>
            <p data-testid="user-scanner-request-health-endpoint-breakdown-1m-summary">
              req={requestEndpointBreakdown.oneMinute.total}, ok/fail={requestEndpointBreakdown.oneMinute.success}/{requestEndpointBreakdown.oneMinute.failed}, success={(requestEndpointBreakdown.oneMinute.successRatio * 100).toFixed(1)}%
            </p>
            <p data-testid="user-scanner-request-health-endpoint-breakdown-1m-excluded">
              excluded(auth+infra)={requestEndpointBreakdown.oneMinute.excluded || 0}
            </p>
            <p data-testid="user-scanner-request-health-endpoint-breakdown-1m-most-impacted">
              most impacted: <span className="font-semibold text-amber-300">{formatEndpointLabel(requestEndpointBreakdown.oneMinute.mostImpactedEndpoint || "n/a")}</span>
            </p>
            <div className="mt-1 grid gap-1" data-testid="user-scanner-request-health-endpoint-breakdown-1m-list">
              {requestEndpointBreakdown.oneMinute.rows.slice(0, 5).map((row, index) => (
                <div key={`one-minute-row-${row.endpoint}`} className="space-y-1" data-testid={`user-scanner-request-health-endpoint-breakdown-1m-item-${index}`}>
                  <p>{formatEndpointLabel(row.endpoint)} → ok/fail {row.success}/{row.fail}</p>
                  <div className="h-1.5 w-full overflow-hidden rounded bg-slate-800" data-testid={`user-scanner-request-health-endpoint-breakdown-1m-sparkbar-${index}`}>
                    <div
                      className="h-full bg-rose-400"
                      style={{ width: `${(endpointFailRatio(row) * 100).toFixed(1)}%` }}
                      data-testid={`user-scanner-request-health-endpoint-breakdown-1m-sparkbar-fill-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded border border-slate-700 bg-slate-950 p-2 text-xs text-slate-300" data-testid="user-scanner-request-health-endpoint-breakdown-5m-card">
            <p className="font-semibold text-cyan-300" data-testid="user-scanner-request-health-endpoint-breakdown-5m-title">Son 5m Endpoint Katkı</p>
            <p data-testid="user-scanner-request-health-endpoint-breakdown-5m-summary">
              req={requestEndpointBreakdown.fiveMinute.total}, ok/fail={requestEndpointBreakdown.fiveMinute.success}/{requestEndpointBreakdown.fiveMinute.failed}, success={(requestEndpointBreakdown.fiveMinute.successRatio * 100).toFixed(1)}%
            </p>
            <p data-testid="user-scanner-request-health-endpoint-breakdown-5m-excluded">
              excluded(auth+infra)={requestEndpointBreakdown.fiveMinute.excluded || 0}
            </p>
            <p data-testid="user-scanner-request-health-endpoint-breakdown-5m-most-impacted">
              most impacted: <span className="font-semibold text-amber-300">{formatEndpointLabel(requestEndpointBreakdown.fiveMinute.mostImpactedEndpoint || "n/a")}</span>
            </p>
            <div className="mt-1 grid gap-1" data-testid="user-scanner-request-health-endpoint-breakdown-5m-list">
              {requestEndpointBreakdown.fiveMinute.rows.slice(0, 5).map((row, index) => (
                <div key={`five-minute-row-${row.endpoint}`} className="space-y-1" data-testid={`user-scanner-request-health-endpoint-breakdown-5m-item-${index}`}>
                  <p>{formatEndpointLabel(row.endpoint)} → ok/fail {row.success}/{row.fail}</p>
                  <div className="h-1.5 w-full overflow-hidden rounded bg-slate-800" data-testid={`user-scanner-request-health-endpoint-breakdown-5m-sparkbar-${index}`}>
                    <div
                      className="h-full bg-rose-400"
                      style={{ width: `${(endpointFailRatio(row) * 100).toFixed(1)}%` }}
                      data-testid={`user-scanner-request-health-endpoint-breakdown-5m-sparkbar-fill-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </section>}

      {scannerLoadDegraded && (
        <div className="order-1 col-span-12 rounded border border-amber-700 bg-amber-950/20 p-3 text-sm text-amber-200" data-testid="user-scanner-degraded-load-banner">
          <p data-testid="user-scanner-degraded-load-banner-summary">
            Kısmi veri yüklendi · auth={scannerFailureSummary.auth_error} · infra={scannerFailureSummary.infra_error}
          </p>
          <p className="mt-1 text-xs text-amber-100" data-testid="user-scanner-degraded-load-banner-endpoints">
            Etkilenen endpointler: {scannerLoadFailures.join(", ") || "-"}
          </p>
        </div>
      )}

      {!SIMPLE_SCANNER_V2 && <section className="order-2 col-span-12 rounded border border-cyan-800/50 bg-cyan-950/20 p-4" data-testid="user-scanner-active-mode-indicator-card">
        <p className="text-xs uppercase tracking-widest text-cyan-300" data-testid="user-scanner-active-mode-indicator-title">Scanner Active Mode Indicator</p>
        <div className="mt-2 grid gap-2 md:grid-cols-4" data-testid="user-scanner-active-mode-indicator-grid">
          <p className="text-sm" data-testid="user-scanner-active-mode-indicator-mode">Active Mode: {activeModeLabel}</p>
          <p className="text-sm font-semibold" data-testid="user-scanner-active-mode-indicator-run-type">Tarama Tipi: {scannerRunType}</p>
          <p className="text-sm" data-testid="user-scanner-active-mode-indicator-path">Execution Path: {executionPathLabel}</p>
          <p className="text-sm" data-testid="user-scanner-active-mode-indicator-source">Source: {symbolSource.toUpperCase()}</p>
          <p className="text-sm" data-testid="user-scanner-active-mode-indicator-symbol-mode">Symbol Mode: {symbolMode}</p>
          <p className="text-sm" data-testid="user-scanner-active-mode-indicator-market-type">Market: {marketType.toUpperCase()}</p>
        </div>
        <p className="mt-2 text-xs text-cyan-100" data-testid="user-scanner-active-mode-indicator-run-type-detail">{scannerRunTypeDetail}</p>
      </section>}

      {/* Pure Live: automation/paper profilleri kaldırıldı */}

      {!SIMPLE_SCANNER_V2 && <section className="order-8 col-span-12 rounded border border-amber-800/50 bg-amber-950/20 p-4" data-testid="user-scanner-auto-alerts-card">
        <p className="text-xs uppercase tracking-widest text-amber-300" data-testid="user-scanner-auto-alerts-title">Otomatik Run Uyarıları</p>
        <div className="mt-2 space-y-1" data-testid="user-scanner-auto-alerts-list">
          {autoRunAlerts.length === 0 && <p className="text-xs text-amber-100" data-testid="user-scanner-auto-alerts-empty">Henüz yeni otomatik sinyal bildirimi yok.</p>}
          {autoRunAlerts.map((item) => (
            <p key={item.id} className="text-xs" data-testid={`user-scanner-auto-alert-item-${item.id}`}>
              {item.profile_name}: +{item.signal_count} sinyal · {formatDateLabel(item.run_at)}
            </p>
          ))}
        </div>
      </section>}

      {!SIMPLE_SCANNER_V2 && <section className="order-9 col-span-12 rounded border border-blue-800/50 bg-blue-950/20 p-4" data-testid="user-decision-card-section">
        <div className="flex items-center justify-between" data-testid="user-decision-card-header">
          <p className="text-xs uppercase tracking-widest text-blue-300" data-testid="user-decision-card-title">Symbol-level Decision Cards</p>
          <div className="flex items-center gap-2" data-testid="user-decision-card-toolbar-actions">
            <p className="text-xs text-blue-100" data-testid="user-decision-card-auto-refresh-label">Auto Refresh: 10s</p>
            <Button variant="outline" onClick={() => load({ silent: true })} data-testid="user-decision-card-refresh-button">Kartları Yenile</Button>
          </div>
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="user-decision-card-grid">
          {decisionCards.length === 0 && <p className="text-xs text-blue-100" data-testid="user-decision-card-empty">Henüz decision card üretilmedi.</p>}
          {decisionCards.map((card) => (
            <DecisionCard
              key={card.symbol}
              card={card}
              onOpenExplainability={onSelectDecisionCard}
              onOpenSymbolDetail={openSymbolDetail}
            />
          ))}
        </div>
      </section>}

      {!SIMPLE_SCANNER_V2 && <section className="order-10 col-span-12 rounded border border-fuchsia-800/50 bg-fuchsia-950/20 p-4" data-testid="user-explainability-panel">
        <p className="text-xs uppercase tracking-widest text-fuchsia-300" data-testid="user-explainability-title">User Explainability Panel</p>
        {!selectedDecisionSymbol && <p className="mt-2 text-xs" data-testid="user-explainability-empty">Önce bir symbol decision card seçin.</p>}
        {selectedDecisionSymbol && (
          <div className="mt-2 flex flex-wrap items-center gap-3" data-testid="user-explainability-content">
            <p className="text-sm" data-testid="user-explainability-selected-symbol">Symbol: {selectedDecisionSymbol}</p>
            <Button type="button" size="sm" variant="outline" onClick={() => setIsExplainabilityDrawerOpen(true)} data-testid="user-explainability-open-drawer-button">
              Explainability Drawer Aç
            </Button>
            {isExplainabilityLoading && <p className="text-xs" data-testid="user-explainability-loading">Yükleniyor...</p>}
            {!isExplainabilityLoading && symbolExplainability && (
              <p className="text-xs text-fuchsia-100" data-testid="user-explainability-final-decision">Final Decision: {symbolExplainability.final_decision}</p>
            )}
          </div>
        )}
      </section>}

      {!SIMPLE_SCANNER_V2 && (
        <ExplainabilityDrawer
          isOpen={isExplainabilityDrawerOpen}
          onOpenChange={setIsExplainabilityDrawerOpen}
          selectedSymbol={selectedDecisionSymbol}
          isLoading={isExplainabilityLoading}
          explainability={symbolExplainability}
          formatDateLabel={formatDateLabel}
        />
      )}

      <section className="order-4 col-span-12 rounded border border-cyan-800/40 bg-cyan-950/10 p-4" data-testid="user-scanner-engine-workflow-panel">
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="user-scanner-engine-workflow-header">
          <div data-testid="user-scanner-engine-workflow-title-wrap">
            <p className="text-xs uppercase tracking-widest text-cyan-300" data-testid="user-scanner-engine-workflow-kicker">Scanner Engine Workflow</p>
            <h3 className="text-base font-semibold" data-testid="user-scanner-engine-workflow-title">Contract-Safe · Cache-First · Karar01-04 Uyumlu Akış</h3>
          </div>
          <p className="text-xs text-cyan-100" data-testid="user-scanner-engine-workflow-readonly-note">Backend contract sabit · Engine version readonly</p>
        </div>

        <div className="mt-3 grid gap-3 xl:grid-cols-4" data-testid="user-scanner-engine-workflow-cards-grid">
          <article className="rounded border border-slate-700 bg-slate-950 p-3" data-testid="user-scanner-engine-contract-card">
            <p className="text-xs uppercase tracking-widest text-slate-400" data-testid="user-scanner-engine-contract-card-title">1) API Contract</p>
            <p className="mt-1 text-sm" data-testid="user-scanner-engine-contract-version">engine_version: <span className="font-semibold">{scannerEngineVersionLabel}</span></p>
            <p className="text-sm" data-testid="user-scanner-engine-contract-run-source">run_source: <span className="font-semibold">{scannerRunSourceLabel}</span></p>
            <p className="text-xs text-slate-400" data-testid="user-scanner-engine-contract-run-id">run_id: {scannerEngineLastRunId}</p>
          </article>

          <article className="rounded border border-slate-700 bg-slate-950 p-3" data-testid="user-scanner-engine-runtime-card">
            <p className="text-xs uppercase tracking-widest text-slate-400" data-testid="user-scanner-engine-runtime-card-title">2-3) Runtime + Delta</p>
            <p className="mt-1 text-sm" data-testid="user-scanner-engine-runtime-decision-mode">Karar Seti: <span className="font-semibold">{scannerDecisionModeSummary}</span></p>
            <p className="text-sm" data-testid="user-scanner-engine-runtime-signal-review-mode">Signal Review Mode: <span className="font-semibold">{signalReviewModeLabel}</span></p>
            <p className="text-sm" data-testid="user-scanner-engine-runtime-scanner-automation-mode">Scanner Automation Mode: <span className="font-semibold">{scannerAutomationModeLabel}</span></p>
            <p className="text-sm" data-testid="user-scanner-engine-runtime-execution-dispatch-mode">Execution Dispatch Mode: <span className="font-semibold">{executionDispatchModeLabel}</span></p>
            <p className="text-xs text-slate-400" data-testid="user-scanner-engine-runtime-next-run">next_preview_run: {formatDateLabel(scannerEngineNextRunAt)}</p>
          </article>

          <article className="rounded border border-slate-700 bg-slate-950 p-3" data-testid="user-scanner-engine-feature-card">
            <p className="text-xs uppercase tracking-widest text-slate-400" data-testid="user-scanner-engine-feature-card-title">4) Feature Layer</p>
            <p className="mt-1 text-sm" data-testid="user-scanner-engine-feature-freshness">freshness: <span className="font-semibold">{scannerFeatureTelemetry.freshnessRatio}%</span></p>
            <p className="text-sm" data-testid="user-scanner-engine-feature-average-age">avg_snapshot_age: <span className="font-semibold">{scannerFeatureTelemetry.averageAge ?? "-"}s</span></p>
            <p className="text-xs text-slate-400" data-testid="user-scanner-engine-feature-stale">stale/sample: {scannerFeatureTelemetry.staleCount}/{scannerFeatureTelemetry.sampleCount}</p>
          </article>

          <article className="rounded border border-slate-700 bg-slate-950 p-3" data-testid="user-scanner-engine-signal-trade-card">
            <p className="text-xs uppercase tracking-widest text-slate-400" data-testid="user-scanner-engine-signal-trade-card-title">5-6) Signal → Trade</p>
            <p className="mt-1 text-sm" data-testid="user-scanner-engine-signal-trade-decisions">long/short/no_trade: <span className="font-semibold">{scannerSignalTradeAlignment.longCount}/{scannerSignalTradeAlignment.shortCount}/{scannerSignalTradeAlignment.noTradeCount}</span></p>
            <p className="text-sm" data-testid="user-scanner-engine-signal-trade-alignment">allocation_blocked: <span className="font-semibold">{scannerSignalTradeAlignment.allocationBlockedCount}</span></p>
            <p className="text-xs text-slate-400" data-testid="user-scanner-engine-signal-trade-tradeable">tradeable/total: {scannerSignalTradeAlignment.tradeableCount}/{scannerSignalTradeAlignment.total}</p>
          </article>
        </div>

        <div className="mt-3 grid gap-2 md:grid-cols-4" data-testid="user-scanner-engine-latency-breakdown-grid">
          <p className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-xs" data-testid="user-scanner-engine-latency-queue">queue_ms: <span className="font-semibold">{scannerLatencyBreakdown.queueLabel}</span></p>
          <p className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-xs" data-testid="user-scanner-engine-latency-feature">feature_ms: <span className="font-semibold">{scannerLatencyBreakdown.featureLabel}</span></p>
          <p className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-xs" data-testid="user-scanner-engine-latency-score">score_ms: <span className="font-semibold">{scannerLatencyBreakdown.scoreLabel}</span></p>
          <p className="rounded border border-slate-700 bg-slate-950 px-3 py-2 text-xs" data-testid="user-scanner-engine-latency-total">total_ms: <span className="font-semibold">{scannerLatencyBreakdown.totalLabel}</span></p>
        </div>
      </section>

      <section className="order-6 col-span-12 space-y-4 rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-scanner-control-section">
        <div data-testid="user-scanner-control-header">
          <p className="text-xs uppercase tracking-widest text-slate-500" data-testid="user-scanner-control-kicker">Scanner Engine</p>
          <h3 className="text-base font-semibold" data-testid="user-scanner-control-title">Short/Long Decoupled Scanner</h3>
        </div>

        <div className="grid gap-3 xl:grid-cols-9 xl:items-start" data-testid="user-scanner-engine-config-grid">
          <label className="space-y-1" data-testid="user-scanner-engine-market-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Market</span>
            <input value="BINANCE" disabled className="h-10 border border-slate-700 bg-slate-950 px-3 py-2 text-sm" data-testid="user-scanner-engine-market-input" />
          </label>

          <label className="space-y-1" data-testid="user-scanner-engine-version-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Engine Version (Readonly)</span>
            <input
              value={scannerEngineVersionLabel}
              disabled
              className="h-10 border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              data-testid="user-scanner-engine-version-input"
            />
          </label>

          <label className="space-y-1" data-testid="user-scanner-engine-decision-mode-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Karar Modu (Canlı)</span>
            <input
              value={scannerDecisionModeSummary}
              disabled
              className="h-10 border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              data-testid="user-scanner-engine-decision-mode-input"
            />
          </label>

          <div className="space-y-2 xl:col-span-2" data-testid="user-scanner-engine-market-types-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Piyasa Seçim Özeti (Salt Okunur)</span>
            <div className="grid gap-2 rounded border border-slate-700 bg-slate-950 p-3 md:grid-cols-2" data-testid="user-scanner-engine-market-selection-summary-grid">
              <p className="text-xs text-slate-300" data-testid="user-scanner-engine-market-selection-summary-spot-status">
                Spot açık: <span className="font-semibold">{String(Boolean(scannerEngineConfig.include_spot))}</span>
              </p>
              <p className="text-xs text-slate-300" data-testid="user-scanner-engine-market-selection-summary-futures-status">
                Futures açık: <span className="font-semibold">{String(Boolean(scannerEngineConfig.include_futures))}</span>
              </p>
              <p className="text-xs text-slate-300" data-testid="user-scanner-engine-market-selection-summary-spot-count">
                Seçili Spot Coin: <span className="font-semibold">{selectedSpotSymbols.length}</span>
              </p>
              <p className="text-xs text-slate-300" data-testid="user-scanner-engine-market-selection-summary-futures-count">
                Seçili Futures Coin: <span className="font-semibold">{selectedFuturesSymbols.length}</span>
              </p>
              <p className="text-xs text-slate-300 md:col-span-2" data-testid="user-scanner-engine-market-selection-summary-source">
                Symbol Source: <span className="font-semibold">{String(symbolSource || "crypto").toUpperCase()}</span>
              </p>
              <p className="text-xs text-slate-400 md:col-span-2" data-testid="user-scanner-engine-market-selection-summary-config-updated-at">
                Son config zamanı: {formatDateLabel(scannerEngineConfig?.updated_at || selectionSavedAt || null)}
              </p>
              <p className="text-[11px] text-cyan-200 md:col-span-2" data-testid="user-scanner-engine-market-selection-summary-source-note">
                Coin seçimi sadece “Scanner Engine / Piyasa Listesi” panelinden yapılır.
              </p>
            </div>
          </div>

          <label className="space-y-1" data-testid="user-scanner-engine-scan-limit-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Scan Limit</span>
            <input
              type="number"
              min={25}
              max={5000}
              value={Number(scannerEngineConfig.scan_limit || 80)}
              onChange={(event) => setScannerEngineConfig((prev) => ({ ...prev, scan_limit: Number(event.target.value || 80) }))}
              disabled={scannerEngineBusy}
              className="h-10 border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              data-testid="user-scanner-engine-scan-limit-input"
            />
          </label>

          <label className="space-y-1" data-testid="user-scanner-engine-batch-size-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Batch Size</span>
            <input
              type="number"
              min={5}
              max={100}
              value={Number(scannerEngineConfig.batch_size || 25)}
              onChange={(event) => setScannerEngineConfig((prev) => ({ ...prev, batch_size: Number(event.target.value || 25) }))}
              disabled={scannerEngineBusy}
              className="h-10 border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              data-testid="user-scanner-engine-batch-size-input"
            />
          </label>

          <div className="space-y-2" data-testid="user-scanner-engine-signal-mode-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Tarama Modu</span>
            <div className="flex h-10 items-center gap-3 border border-slate-700 bg-slate-950 px-3 py-2" data-testid="user-scanner-engine-signal-mode-checkbox-wrap">
              <label className="flex items-center gap-1 text-xs" data-testid="user-scanner-engine-signal-mode-auto-label">
                <input
                  type="checkbox"
                  checked={String(scannerEngineConfig.signal_mode || "manual").toLowerCase() === "auto"}
                  onChange={(event) => setScannerEngineConfig((prev) => ({ ...prev, signal_mode: event.target.checked ? "auto" : "manual" }))}
                  disabled={scannerEngineBusy}
                  data-testid="user-scanner-engine-signal-mode-auto-checkbox"
                />
                Full Auto
              </label>
              <span className="ml-auto text-[11px] text-cyan-200" data-testid="user-scanner-engine-signal-mode-fixed-note">Auto kapalıysa sinyal manuel/assisted modda üretilir</span>
            </div>
          </div>

          <label className="space-y-1" data-testid="user-scanner-run-market-type-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Scanner Market</span>
            <select
              value={String(marketType || "spot").toLowerCase()}
              onChange={(event) => setMarketType(String(event.target.value || "spot").toLowerCase())}
              disabled={scannerEngineBusy || scannerRunBusy}
              className="h-10 w-full border border-slate-700 bg-slate-950 px-2 text-sm"
              data-testid="user-scanner-run-market-type-select"
            >
              <option value="spot" data-testid="user-scanner-run-market-type-option-spot">spot</option>
              <option value="futures" data-testid="user-scanner-run-market-type-option-futures">futures</option>
              <option value="both" data-testid="user-scanner-run-market-type-option-both">both</option>
            </select>
          </label>

          <label className="space-y-1" data-testid="user-scanner-engine-auto-interval-field">
            <span className="text-xs uppercase tracking-widest text-slate-500">Auto Tarama Süresi</span>
            <select
              value={selectedAutoIntervalMinutes}
              onChange={(event) => setScannerEngineConfig((prev) => ({
                ...prev,
                auto_interval_minutes: AUTO_INTERVAL_MINUTE_OPTIONS.includes(Number(event.target.value))
                  ? Number(event.target.value)
                  : 3,
              }))}
              disabled={scannerEngineBusy}
              className="h-10 w-full border border-slate-700 bg-slate-950 px-2 text-sm"
              data-testid="user-scanner-engine-auto-interval-select"
            >
              <option value={1} data-testid="user-scanner-engine-auto-interval-option-1m">1 dakika</option>
              <option value={3} data-testid="user-scanner-engine-auto-interval-option-3m">3 dakika</option>
              <option value={5} data-testid="user-scanner-engine-auto-interval-option-5m">5 dakika</option>
            </select>
          </label>

          <div className="space-y-2 xl:col-span-2" data-testid="user-scanner-auto-run-toggle-wrap">
            <span className="text-xs uppercase tracking-widest text-slate-500">Otomatik Tetik</span>
            <div className="grid gap-2 rounded border border-slate-700 bg-slate-950 p-3">
              <label className="flex items-center justify-between gap-2 text-xs" data-testid="user-scanner-auto-engine-preview-toggle-label">
                <span>Auto Engine Preview</span>
                <input
                  type="checkbox"
                  checked={autoEnginePreviewEnabled}
                  onChange={(event) => setAutoEnginePreviewEnabled(Boolean(event.target.checked))}
                  data-testid="user-scanner-auto-engine-preview-toggle"
                />
              </label>
              <label className="flex items-center justify-between gap-2 text-xs" data-testid="user-scanner-auto-scanner-run-toggle-label">
                <span>Auto Scanner Run</span>
                <input
                  type="checkbox"
                  checked={autoScannerRunEnabled}
                  onChange={(event) => setAutoScannerRunEnabled(Boolean(event.target.checked))}
                  data-testid="user-scanner-auto-scanner-run-toggle"
                />
              </label>
            </div>
            {autoScannerBlocked && (
              <p className="rounded border border-amber-700 bg-amber-950/30 px-2 py-1 text-xs text-amber-200" data-testid="user-scanner-auto-scanner-run-blocked-warning">
                Auto scanner aktif ancak seçili market için sembol yok. Tarama çalışmayacak.
              </p>
            )}
          </div>

          <div className="space-y-1" data-testid="user-scanner-engine-save-action-wrap">
            <span className="text-xs uppercase tracking-widest text-slate-500" data-testid="user-scanner-engine-save-label">Kaydet</span>
            <Button type="button" variant="outline" onClick={saveScannerEngineConfig} disabled={scannerEngineBusy} className="h-10 w-full" data-testid="user-scanner-engine-save-button">Kaydet</Button>
          </div>

          <div className="space-y-1" data-testid="user-scanner-engine-run-action-wrap">
            <span className="text-xs uppercase tracking-widest text-slate-500" data-testid="user-scanner-engine-run-label">Engine Preview</span>
            <Button type="button" variant="outline" onClick={runScannerEngine} disabled={scannerEngineBusy} className="h-10 w-full" data-testid="user-scanner-engine-run-button">Preview Tara</Button>
          </div>

          <div className="space-y-1" data-testid="user-scanner-runtime-run-action-wrap">
            <span className="text-xs uppercase tracking-widest text-slate-500" data-testid="user-scanner-runtime-run-label">Gerçek Scanner</span>
            <Button type="button" variant="outline" onClick={() => runScannerRuntime({ reason: "user_scanner_manual_run" })} disabled={scannerRunBusy} className="h-10 w-full" data-testid="user-scanner-runtime-run-button">Scanner Tara</Button>
          </div>
        </div>

        <div className="grid gap-2 rounded border border-slate-800 bg-slate-950 p-3 md:grid-cols-2" data-testid="user-scanner-live-scan-timer-section">
          <p className="text-sm" data-testid="user-scanner-last-preview-run-value">Last Preview Run: {formatDateLabel(scannerEngineRun?.generated_at)}</p>
          <p className="text-sm" data-testid="user-scanner-next-preview-run-value">Next Preview Run: {autoEnginePreviewEnabled ? formatDateLabel(scannerEngineNextRunAt) : "AUTO_PREVIEW_OFF"}</p>
          <p className="text-sm" data-testid="user-scanner-last-scanner-run-value">Last Scanner Run: {formatDateLabel(scannerRuntimeLastRunAt)}</p>
          <p className="text-sm" data-testid="user-scanner-next-scanner-run-value">Next Scanner Run: {scannerRuntimeNextRunAt === "BLOCKED_NO_SYMBOL" ? "BLOCKED (SEMBOL YOK)" : (autoScannerRunEnabled ? formatDateLabel(scannerRuntimeNextRunAt) : "AUTO_SCANNER_OFF")}</p>
        </div>

        <div className="grid gap-2 rounded border border-slate-800 bg-slate-950 p-3 text-xs text-slate-300 md:grid-cols-2" data-testid="user-scanner-run-types-help-box">
          <p data-testid="user-scanner-preview-run-help">Preview Tara → signal üretmez (sadece ranking/preview).</p>
          <p data-testid="user-scanner-runtime-run-help">Scanner Tara → gerçek scanner pipeline (signal materialization).</p>
        </div>
      </section>

      <section className="order-7 col-span-12 space-y-3 rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-scanner-symbol-selector-section">
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="user-scanner-symbol-selector-header">
          <div>
            <p className="text-xs uppercase tracking-widest text-cyan-300" data-testid="user-scanner-symbol-selector-kicker">Scanner Engine / Piyasa Listesi</p>
            <h3 className="text-sm font-semibold" data-testid="user-scanner-symbol-selector-title">Spot/Futures için USDT coin seçimi</h3>
          </div>
          <label className="flex items-center gap-2 text-xs" data-testid="user-scanner-symbol-selector-market-type-field">
            <span className="text-slate-400">Piyasa Tipi</span>
            <select
              value={symbolSelectorMarketType}
              onChange={(event) => setSymbolSelectorMarketType(event.target.value)}
              className="h-9 rounded border border-slate-700 bg-black px-2"
              data-testid="user-scanner-symbol-selector-market-type-select"
            >
              <option value="spot" data-testid="user-scanner-symbol-selector-market-type-option-spot">spot</option>
              <option value="futures" data-testid="user-scanner-symbol-selector-market-type-option-futures">futures</option>
            </select>
          </label>
        </div>

        <SymbolSelectorPanel
          testIdPrefix="user-scanner-market-symbol-selector"
          exchange="binance"
          marketType={symbolSelectorMarketType}
          quoteAssetFilter="USDT"
          multi
          selectedSymbols={symbolSelectorMarketType === "spot" ? selectedSpotSymbols : selectedFuturesSymbols}
          onSelectedSymbolsChange={(next) => {
            const normalizedNext = Array.from(new Set((next || []).map((item) => String(item || "").trim().toUpperCase()).filter(Boolean)));
            setEngineSymbolSelection((prev) => ({
              ...prev,
              [symbolSelectorMarketType === "spot" ? "spot" : "futures"]: normalizedNext,
            }));
          }}
          source={symbolSource}
          onSourceChange={setSymbolSource}
          mode={symbolMode}
          onModeChange={setSymbolMode}
          onPersistSelection={async (payload) => {
            try {
              const normalizedSymbols = Array.from(new Set((payload?.selected_symbols || []).map((item) => String(item || "").trim().toUpperCase()).filter(Boolean)));
              const normalizedMarketType = String(payload?.market_type || symbolSelectorMarketType || "spot").toLowerCase();
              const requestPayload = {
                scanner_id: "default",
                market_type: normalizedMarketType,
                symbol_source: String(payload?.symbol_source || symbolSource || "crypto").toLowerCase(),
                symbol_selection_mode: String(payload?.symbol_selection_mode || symbolMode || "manual_selection").toLowerCase(),
                selected_symbols: normalizedSymbols,
                list_name: payload?.list_name || null,
              };
              const response = await apiClient.put("/user/scanner/symbol-selection", requestPayload, { timeout: 15000 });
              const saved = response?.data || {};
              const normalizedSaved = Array.from(new Set((saved?.selected_symbols || normalizedSymbols).map((item) => String(item || "").trim().toUpperCase()).filter(Boolean)));
              setEngineSymbolSelection((prev) => ({
                ...prev,
                [normalizedMarketType === "futures" ? "futures" : "spot"]: normalizedSaved,
              }));
              setSelectionSavedAt(saved?.saved_at || new Date().toISOString());
              await load({ silent: true });
              toast.success("Symbol selection backend'e kaydedildi");
              return true;
            } catch (error) {
              toast.error(toApiErrorMessage(error, "Symbol selection kaydedilemedi"));
              return false;
            }
          }}
        />
      </section>

      <section className="order-8 col-span-12 rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-scanner-parameters-card">
        <p className="text-xs uppercase tracking-widest text-slate-400" data-testid="user-scanner-parameters-title">Çalışma Parametreleri</p>
        <div className="mt-2 grid gap-2 md:grid-cols-3" data-testid="user-scanner-parameters-grid">
          <p className="text-sm" data-testid="user-scanner-parameters-market">Market: <span className="font-semibold">{String(marketType || "spot").toUpperCase()}</span></p>
          <p className="text-sm" data-testid="user-scanner-parameters-signal-review-mode">Signal Review Mode: <span className="font-semibold">{signalReviewModeLabel}</span></p>
          <p className="text-sm" data-testid="user-scanner-parameters-scanner-automation-mode">Scanner Automation Mode: <span className="font-semibold">{scannerAutomationModeLabel}</span></p>
          <p className="text-sm" data-testid="user-scanner-parameters-execution-dispatch-mode">Execution Dispatch Mode: <span className="font-semibold">{executionDispatchModeLabel}</span></p>
          <p className="text-sm" data-testid="user-scanner-parameters-interval">Interval: <span className="font-semibold">{selectedAutoIntervalMinutes} dk</span></p>
          <p className="text-sm" data-testid="user-scanner-parameters-selected-count">Selected Symbols: <span className="font-semibold">{selectedSymbols.length}</span></p>
          <p className="text-sm" data-testid="user-scanner-parameters-max-results">Max Results: <span className="font-semibold">{SIMPLE_SCANNER_V2 ? 120 : 25}</span></p>
          <p className="text-sm md:col-span-3" data-testid="user-scanner-parameters-selected-sample">
            Örnek Semboller: <span className="font-mono text-xs">{(selectedSymbols || []).slice(0, 12).join(", ") || "-"}{selectedSymbols.length > 12 ? ` +${selectedSymbols.length - 12}` : ""}</span>
          </p>
          <p className="text-sm md:col-span-3" data-testid="user-scanner-parameters-strategy-mapping">Strategy Mapping: <span className="font-semibold">signal.execution_strategy_type = bot.strategy_type</span></p>
        </div>
      </section>

      {!SIMPLE_SCANNER_V2 && <section className="order-7 col-span-12 space-y-3 rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-scanner-minimal-filter-section">
        <div data-testid="user-scanner-minimal-filter-header">
          <p className="text-xs uppercase tracking-widest text-slate-500" data-testid="user-scanner-minimal-filter-kicker">Filter Layer</p>
          <h3 className="text-base font-semibold" data-testid="user-scanner-minimal-filter-title">Minimal Set</h3>
        </div>

        <div className="grid grid-cols-12 gap-3" data-testid="user-scanner-minimal-filter-grid">
          <label className="col-span-6 md:col-span-2" data-testid="user-scanner-filter-rsi-min-field">
            <span className="text-xs text-slate-400" data-testid="user-scanner-filter-rsi-min-label">rsi_min</span>
            <input
              type="number"
              value={minimalFilters.rsi_min}
              onChange={(event) => setMinimalFilters((prev) => ({ ...prev, rsi_min: event.target.value }))}
              className="mt-1 h-10 w-full rounded border border-slate-700 bg-slate-950 px-3 text-sm"
              data-testid="user-scanner-filter-rsi-min-input"
            />
          </label>
          <label className="col-span-6 md:col-span-2" data-testid="user-scanner-filter-rsi-max-field">
            <span className="text-xs text-slate-400" data-testid="user-scanner-filter-rsi-max-label">rsi_max</span>
            <input
              type="number"
              value={minimalFilters.rsi_max}
              onChange={(event) => setMinimalFilters((prev) => ({ ...prev, rsi_max: event.target.value }))}
              className="mt-1 h-10 w-full rounded border border-slate-700 bg-slate-950 px-3 text-sm"
              data-testid="user-scanner-filter-rsi-max-input"
            />
          </label>
          <label className="col-span-6 md:col-span-3" data-testid="user-scanner-filter-volume-min-field">
            <span className="text-xs text-slate-400" data-testid="user-scanner-filter-volume-min-label">volume_min</span>
            <input
              type="number"
              value={minimalFilters.volume_min}
              onChange={(event) => setMinimalFilters((prev) => ({ ...prev, volume_min: event.target.value }))}
              className="mt-1 h-10 w-full rounded border border-slate-700 bg-slate-950 px-3 text-sm"
              data-testid="user-scanner-filter-volume-min-input"
            />
          </label>
          <label className="col-span-6 md:col-span-3" data-testid="user-scanner-filter-market-cap-min-field">
            <span className="text-xs text-slate-400" data-testid="user-scanner-filter-market-cap-min-label">market_cap_min</span>
            <input
              type="number"
              value={minimalFilters.market_cap_min}
              onChange={(event) => setMinimalFilters((prev) => ({ ...prev, market_cap_min: event.target.value }))}
              className="mt-1 h-10 w-full rounded border border-slate-700 bg-slate-950 px-3 text-sm"
              data-testid="user-scanner-filter-market-cap-min-input"
            />
          </label>
          <label className="col-span-12 md:col-span-2" data-testid="user-scanner-filter-timeframe-field">
            <span className="text-xs text-slate-400" data-testid="user-scanner-filter-timeframe-label">timeframe</span>
            <select
              value={minimalFilters.timeframe}
              onChange={(event) => setMinimalFilters((prev) => ({ ...prev, timeframe: event.target.value }))}
              className="mt-1 h-10 w-full rounded border border-slate-700 bg-slate-950 px-3 text-sm"
              data-testid="user-scanner-filter-timeframe-select"
            >
              <option value="15m" data-testid="user-scanner-filter-timeframe-option-15m">15m</option>
              <option value="1h" data-testid="user-scanner-filter-timeframe-option-1h">1h</option>
              <option value="4h" data-testid="user-scanner-filter-timeframe-option-4h">4h</option>
              <option value="1d" data-testid="user-scanner-filter-timeframe-option-1d">1d</option>
            </select>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="user-scanner-minimal-filter-chip-row">
          {activeMinimalFilterChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex rounded-full border border-cyan-500/60 bg-cyan-500/15 px-2 py-1 text-xs text-cyan-100"
              data-testid={`user-scanner-filter-chip-${chip.key}`}
            >
              {chip.label}
            </span>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setMinimalFilters(MINIMAL_FILTER_DEFAULTS)}
            data-testid="user-scanner-filter-clear-all-button"
          >
            Clear All
          </Button>
        </div>
      </section>}

      {/* Pure Live: preset runner kaldırıldı */}

      {!SIMPLE_SCANNER_V2 && <section className="order-11 col-span-12 space-y-3 rounded border border-cyan-800/50 bg-cyan-950/20 p-4" data-testid="user-scanner-live-readiness-section">
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="user-scanner-live-readiness-header">
          <div data-testid="user-scanner-live-readiness-title-wrap">
            <p className="text-xs uppercase tracking-widest text-cyan-300" data-testid="user-scanner-live-readiness-kicker">Live Readiness</p>
            <h3 className="text-base font-semibold" data-testid="user-scanner-live-readiness-title">Scanner → Execution Güvenlik Kontrolleri</h3>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="user-scanner-live-readiness-export-actions">
            <Button variant="outline" onClick={() => exportScannerDailyReport("json")} data-testid="user-scanner-live-readiness-export-json-button">Export JSON</Button>
            <Button variant="outline" onClick={() => exportScannerDailyReport("csv")} data-testid="user-scanner-live-readiness-export-csv-button">Export CSV</Button>
          </div>
        </div>

        <div className="grid gap-2 md:grid-cols-3" data-testid="user-scanner-live-readiness-grid">
          <article className="rounded border border-cyan-900 bg-slate-950 p-3" data-testid="user-scanner-live-readiness-symbol-integrity-card">
            <p className="text-xs text-cyan-200" data-testid="user-scanner-live-readiness-symbol-integrity-label">Symbol Integrity</p>
            <p className="text-sm font-semibold" data-testid="user-scanner-live-readiness-symbol-integrity-value">
              {liveReadiness?.symbol_integrity?.ok ? "OK" : "MISMATCH"} ({liveReadiness?.symbol_integrity?.matched ?? 0}/{liveReadiness?.symbol_integrity?.checked ?? 0})
            </p>
          </article>
          <article className="rounded border border-cyan-900 bg-slate-950 p-3" data-testid="user-scanner-live-readiness-risk-guard-card">
            <p className="text-xs text-cyan-200" data-testid="user-scanner-live-readiness-risk-guard-label">Max Risk Guard</p>
            <p className="text-sm font-semibold" data-testid="user-scanner-live-readiness-risk-guard-value">
              max_positions=3 | daily_loss_limit=1% | daily_loss={liveReadiness?.max_risk_guard?.daily_loss_pct ?? 0}%
            </p>
          </article>
          <article className="rounded border border-cyan-900 bg-slate-950 p-3" data-testid="user-scanner-live-readiness-execution-quality-card">
            <p className="text-xs text-cyan-200" data-testid="user-scanner-live-readiness-execution-quality-label">Execution Quality</p>
            <p className="text-sm font-semibold" data-testid="user-scanner-live-readiness-execution-quality-value">
              latency={liveReadiness?.execution_quality?.avg_latency ?? 0}ms | reject={liveReadiness?.execution_quality?.reject_rate ?? 0}
            </p>
          </article>
        </div>

        <pre className="overflow-x-auto rounded border border-cyan-900 bg-slate-950 p-3 text-xs text-cyan-100" data-testid="user-scanner-daily-report-json">
          {JSON.stringify(scannerDailyReport || {}, null, 2)}
        </pre>
      </section>}

      <section className="order-12 col-span-12" data-testid="user-scanner-results-main-section">
        <div className="rounded border border-slate-800 bg-slate-900 p-4" data-testid="user-scanner-result-split-card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2" data-testid="user-scanner-result-split-header">
            <div data-testid="user-scanner-result-split-title-wrap">
              <p className="text-xs uppercase tracking-widest text-cyan-300" data-testid="user-scanner-result-split-kicker">FAZ 2 Ayrık Akış</p>
              <h3 className="text-sm font-semibold" data-testid="user-scanner-result-split-title">Engine Preview vs Scanner Signals</h3>
              <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="user-scanner-engine-result-badge-wrap">
                <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold uppercase ${engineResultBadge.className}`} data-testid="user-scanner-engine-result-origin-badge">
                  {engineResultBadge.label}
                </span>
                <span className="text-[11px] text-slate-400" data-testid="user-scanner-engine-result-generated-at">
                  generated_at: {formatDateLabel(scannerEngineRun?.generated_at || null)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2" data-testid="user-scanner-result-split-tab-buttons">
              <Button
                type="button"
                variant={scannerResultTab === "signals" ? "default" : "outline"}
                onClick={() => setScannerResultTab("signals")}
                data-testid="user-scanner-result-tab-signals-button"
              >
                Scanner Signals ({scannerSignals.length})
              </Button>
              <Button
                type="button"
                variant={scannerResultTab === "engine" ? "default" : "outline"}
                onClick={() => setScannerResultTab("engine")}
                data-testid="user-scanner-result-tab-engine-button"
              >
                Engine Preview ({enginePreviewRows.length})
              </Button>
            </div>
          </div>

          <div className="mb-3 grid gap-2 md:grid-cols-2" data-testid="user-scanner-preview-signal-product-contract">
            <article className="rounded border border-cyan-800/50 bg-cyan-950/20 px-3 py-2" data-testid="user-scanner-preview-contract-card">
              <p className="text-[11px] uppercase tracking-widest text-cyan-200" data-testid="user-scanner-preview-contract-title">Engine Preview Contract</p>
              <p className="text-xs text-cyan-100" data-testid="user-scanner-preview-contract-endpoint">endpoint: /api/user/scanner-engine/preview</p>
              <p className="text-xs text-cyan-100" data-testid="user-scanner-preview-contract-scope">scope: engine_preview_only · creates_signals=false</p>
              <p className="text-xs text-cyan-100" data-testid="user-scanner-preview-contract-count">items: {enginePreviewRows.length}</p>
              <p className="text-xs text-cyan-100" data-testid="user-scanner-preview-contract-meta-source">source: {previewMetaSource}</p>
              <p className="text-xs text-cyan-100" data-testid="user-scanner-preview-contract-meta-generated-at">generated_at: {formatDateLabel(scannerEngineRun?.generated_at)}</p>
              <p className="text-xs text-cyan-100" data-testid="user-scanner-preview-contract-meta-auto-enabled">auto_preview_enabled: {String(Boolean(autoEnginePreviewEnabled))}</p>
              <p className="text-xs text-cyan-100" data-testid="user-scanner-preview-contract-meta-manual-selection-count">manual_selection_count: {selectedSymbols.length}</p>
            </article>
            <article className="rounded border border-emerald-800/50 bg-emerald-950/20 px-3 py-2" data-testid="user-scanner-signal-contract-card">
              <p className="text-[11px] uppercase tracking-widest text-emerald-200" data-testid="user-scanner-signal-contract-title">Scanner Signals Contract</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-endpoint">endpoint: /api/user/signals</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-scope">scope: actionable_signal_stream · creates_signals=true</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-count">items: {scannerSignals.length}</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-reason-code">scanner_result_reason: {scannerReasonCode || "-"}</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-reason-source">source: {scannerStatusContract?.scanner_result_source || "-"}</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-reason-severity">severity: {scannerStatusContract?.scanner_result_severity || "-"}</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-reason-retryable">retryable: {String(scannerStatusContract?.scanner_result_retryable ?? "-")}</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-reason-message">reason_message: {scannerReasonText}</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-reason-hint">hint: {scannerStatusContract?.scanner_result_hint || "-"}</p>
              <p className="text-xs text-emerald-100" data-testid="user-scanner-signal-contract-materialized-count">signal_materialized_count: {Number(scannerStatusContract?.signal_materialized_count || 0)}</p>
              {scannerReasonCode === "trade_blocker_active" && (
                <p className="text-xs text-amber-200" data-testid="user-scanner-signal-contract-trade-blocker-message">
                  Trade blocker aktif; signal materialization veya dispatch kısıtlanabilir. Son tetik: {formatDateLabel(scannerRuntimeLastRunAt)}
                </p>
              )}
            </article>
          </div>

          {scannerResultTab === "signals" ? (
            <div className="overflow-x-auto" data-testid="user-scanner-signals-table-wrap">
              <table className="min-w-full text-sm" data-testid="user-scanner-signals-table">
                <thead data-testid="user-scanner-signals-table-head">
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-400" data-testid="user-scanner-signals-table-head-row">
                    <th className="px-2 py-2" data-testid="user-scanner-signals-head-symbol">Symbol</th>
                    <th className="px-2 py-2" data-testid="user-scanner-signals-head-signal">Signal</th>
                    <th className="px-2 py-2" data-testid="user-scanner-signals-head-source">Source</th>
                    <th className="px-2 py-2" data-testid="user-scanner-signals-head-status">Status</th>
                    <th className="px-2 py-2" data-testid="user-scanner-signals-head-tradeable">Tradeable</th>
                    <th className="px-2 py-2" data-testid="user-scanner-signals-head-blocked-reason">Blocked Reason</th>
                    <th className="px-2 py-2" data-testid="user-scanner-signals-head-created-at">Created At</th>
                  </tr>
                </thead>
                <tbody data-testid="user-scanner-signals-table-body">
                  {scannerSignals.length === 0 ? (
                    <tr data-testid="user-scanner-signals-empty-row">
                      <td colSpan={7} className="px-2 py-3 text-xs text-slate-500" data-testid="user-scanner-signals-empty-text">Scanner signal bulunamadı.</td>
                    </tr>
                  ) : scannerSignals.map((row) => (
                    <tr key={row.id} className="border-b border-slate-900/80" data-testid={`user-scanner-signals-row-${row.id}`}>
                      <td className="px-2 py-2" data-testid={`user-scanner-signals-symbol-${row.id}`}>
                        <button
                          type="button"
                          className="font-mono text-cyan-200 hover:text-cyan-100"
                          onClick={() => onSelectDecisionCard(row.symbol)}
                          data-testid={`user-scanner-signals-symbol-button-${row.id}`}
                        >
                          {row.symbol}
                        </button>
                      </td>
                      <td className="px-2 py-2 font-semibold" data-testid={`user-scanner-signals-signal-${row.id}`}>{row.signal}</td>
                      <td className="px-2 py-2" data-testid={`user-scanner-signals-source-${row.id}`}>{row.source}</td>
                      <td className="px-2 py-2" data-testid={`user-scanner-signals-status-${row.id}`}>
                        <span className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs" data-testid={`user-scanner-signals-status-badge-${row.id}`}>
                          {row.status} ({row.raw_status})
                        </span>
                      </td>
                      <td className="px-2 py-2" data-testid={`user-scanner-signals-tradeable-${row.id}`}>{String(Boolean(row.tradeable))}</td>
                      <td className="px-2 py-2" data-testid={`user-scanner-signals-blocked-reason-${row.id}`}>{row.blocked_reason}</td>
                      <td className="px-2 py-2 text-xs text-slate-400" data-testid={`user-scanner-signals-created-at-${row.id}`}>{formatDateLabel(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="user-scanner-engine-preview-table-wrap">
              <table className="min-w-full text-sm" data-testid="user-scanner-engine-preview-table">
                <thead data-testid="user-scanner-engine-preview-table-head">
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-400" data-testid="user-scanner-engine-preview-table-head-row">
                    <th className="px-2 py-2" data-testid="user-scanner-engine-preview-head-rank">Rank</th>
                    <th className="px-2 py-2" data-testid="user-scanner-engine-preview-head-candidate">Candidate</th>
                    <th className="px-2 py-2" data-testid="user-scanner-engine-preview-head-score">Score</th>
                    <th className="px-2 py-2" data-testid="user-scanner-engine-preview-head-market">Market</th>
                    <th className="px-2 py-2" data-testid="user-scanner-engine-preview-head-tier">Tier</th>
                    <th className="px-2 py-2" data-testid="user-scanner-engine-preview-head-preview-status">Preview Status</th>
                  </tr>
                </thead>
                <tbody data-testid="user-scanner-engine-preview-table-body">
                  {enginePreviewRows.length === 0 ? (
                    <tr data-testid="user-scanner-engine-preview-empty-row">
                      <td colSpan={6} className="px-2 py-3 text-xs text-slate-500" data-testid="user-scanner-engine-preview-empty-text">Engine preview sonucu bulunamadı.</td>
                    </tr>
                  ) : enginePreviewRows.map((row) => (
                    <tr key={row.id} className="border-b border-slate-900/80" data-testid={`user-scanner-engine-preview-row-${row.id}`}>
                      <td className="px-2 py-2" data-testid={`user-scanner-engine-preview-rank-${row.id}`}>{row.rank}</td>
                      <td className="px-2 py-2 font-mono text-cyan-200" data-testid={`user-scanner-engine-preview-candidate-${row.id}`}>{row.candidate}</td>
                      <td className="px-2 py-2 font-semibold" data-testid={`user-scanner-engine-preview-score-${row.id}`}>{Number(row.score || 0).toFixed(2)}</td>
                      <td className="px-2 py-2" data-testid={`user-scanner-engine-preview-market-${row.id}`}>{String(row.market_type || "spot")}</td>
                      <td className="px-2 py-2" data-testid={`user-scanner-engine-preview-tier-${row.id}`}>{String(row.candidate_tier || "tier_c")}</td>
                      <td className="px-2 py-2" data-testid={`user-scanner-engine-preview-preview-status-${row.id}`}>{String(row.preview_status || engineResultBadge.label || "preview")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      </>
      )}
    </section>
  );
};

const UX_AUTO_MODES = [
  { value: "off", label: "Kapalı", description: "Otomasyon kapalı. Sadece manuel aksiyon." },
  { value: "preview", label: "Preview", description: "Sadece preview üretir, sinyal yazmaz." },
  { value: "signal", label: "Signal", description: "Scanner/signal akışı çalışır." },
  { value: "full_auto", label: "Full Auto", description: "Preview + Scanner birlikte çalışır." },
];

const UX_MARKETS = [
  { value: "spot", label: "Spot" },
  { value: "futures", label: "Futures" },
  { value: "both", label: "Both" },
];

const formatDateTimeShort = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("tr-TR", {
    hour12: false,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeSignalRowsForUx = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row, index) => ({
      id: String(row?.id || `${row?.signal_id || "signal"}-${index}`),
      symbol: String(row?.symbol || "").toUpperCase(),
      signal: String(row?.signal || row?.signal_direction || "-").toUpperCase(),
      status: String(row?.status || "-").toLowerCase(),
      tradeable: Boolean(row?.tradeable ?? row?.execution_eligible),
      reason: String(row?.blocked_reason_code || row?.first_precheck_failure_code || "-").toUpperCase() || "-",
      created_at: row?.created_at || row?.signal_generated_at || null,
      // FAZ 2 + 3: Volume+Momentum + Tier alanları
      tier: String(row?.tier || "-").toUpperCase(),
      quality_score: Number(row?.quality_score) || null,
      ranking_score: Number(row?.ranking_score) || null,
      volume_24h: Number(row?.volume_24h) || null,
      volume_ratio: Number(row?.volume_ratio) || null,
      cmf: Number(row?.cmf) || null,
      confidence: Number(row?.confidence) || null,
    }))
    .filter((row) => Boolean(row.symbol));
};

// Tier badge renkleri
const TIER_BADGE_COLORS = {
  S: "bg-emerald-500/20 text-emerald-700 border border-emerald-500/40",
  A: "bg-green-500/20 text-green-700 border border-green-500/40",
  B: "bg-yellow-500/20 text-yellow-700 border border-yellow-500/40",
  C: "bg-orange-500/20 text-orange-700 border border-orange-500/40",
  D: "bg-red-500/20 text-red-700 border border-red-500/40",
};

const formatShortNumber = (num) => {
  const n = Number(num);
  if (!Number.isFinite(n) || n === 0) return "-";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(2);
};

const normalizePreviewRowsForUx = (payload) => {
  const rows = payload?.results || payload?.top_results || [];
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row, index) => ({
      id: `${row?.symbol || "candidate"}-${index}`,
      symbol: String(row?.symbol || row?.candidate || "").toUpperCase(),
      score: Number(row?.score || row?.long_score || row?.short_score || 0),
      tier: String(row?.candidate_tier || "tier_c"),
      market_type: String(row?.market_type || "spot"),
    }))
    .filter((row) => Boolean(row.symbol));
};

export const UserScannerPage = () => {
  const navigate = useNavigate();
  const [marketType, setMarketType] = useState("");
  const [autoMode, setAutoMode] = useState("off");
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [resultsTab, setResultsTab] = useState("signals");
  const [lastPreviewAt, setLastPreviewAt] = useState(null);
  const [lastScannerAt, setLastScannerAt] = useState(null);
  const [explainSelf, setExplainSelf] = useState(null);
  const [scopeSummary, setScopeSummary] = useState({});
  const [signalRows, setSignalRows] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [scopeDraftSymbols, setScopeDraftSymbols] = useState([]);
  const [automationConfig, setAutomationConfig] = useState(null);
  const [scopeEditorOpen, setScopeEditorOpen] = useState(false);
  const hasMarketScope = marketType === "spot" || marketType === "futures" || marketType === "both";

  const activeAutoMode = useMemo(() => UX_AUTO_MODES.find((mode) => mode.value === autoMode) || UX_AUTO_MODES[0], [autoMode]);

  const loadExplainSelf = useCallback(async (selectedMarket) => {
    const response = await apiClient.get("/user/control/explain-self", { params: { market_type: selectedMarket }, timeout: 12000 });
    setExplainSelf(response?.data || {});
  }, []);

  const loadScopeSummary = useCallback(async (selectedMarket) => {
    const response = await apiClient.get("/user/scanner/summary", { params: { market_type: selectedMarket }, timeout: 12000 });
    const payload = response?.data || {};
    setScopeSummary(payload);
    setScopeDraftSymbols(Array.isArray(payload.selected_symbols) ? payload.selected_symbols : []);
  }, []);

  const persistSymbolSelection = useCallback(async (selectionPayload = {}) => {
    const enforcedMarketType = String(selectionPayload?.market_type || marketType || "").trim().toLowerCase();
    if (!enforcedMarketType || !["spot", "futures", "both"].includes(enforcedMarketType)) {
      toast.error("Önce market scope seçin (Spot/Futures/Both)");
      return false;
    }
    const normalizedSymbols = Array.isArray(selectionPayload?.selected_symbols)
      ? Array.from(new Set(selectionPayload.selected_symbols.map((item) => String(item || "").trim().toUpperCase()).filter(Boolean)))
      : [];
    const modeToken = String(selectionPayload?.symbol_selection_mode || "manual_selection").trim().toLowerCase();
    const listNameToken = String(selectionPayload?.list_name || "").trim();
    const payload = {
      scanner_id: "default",
      market_type: enforcedMarketType,
      symbol_source: String(selectionPayload?.symbol_source || scopeSummary?.source_mode || "crypto").trim().toLowerCase(),
      symbol_selection_mode: modeToken,
      list_name: listNameToken || (modeToken === "manual_selection" ? "manual-selection" : modeToken === "top_volume" ? "top-volume" : "all-market"),
      selected_symbols: normalizedSymbols,
      spot_selected_symbols: enforcedMarketType === "both"
        ? (Array.isArray(scopeSummary?.spot_selected_symbols) ? scopeSummary.spot_selected_symbols : normalizedSymbols)
        : [],
      futures_selected_symbols: enforcedMarketType === "both"
        ? (Array.isArray(scopeSummary?.futures_selected_symbols) ? scopeSummary.futures_selected_symbols : normalizedSymbols)
        : [],
    };

    try {
      const response = await apiClient.put("/user/scanner/symbol-selection", payload, { timeout: 15000 });
      const saved = response?.data || {};
      setScopeDraftSymbols(Array.isArray(saved?.selected_symbols) ? saved.selected_symbols : normalizedSymbols);
      await Promise.all([loadScopeSummary(payload.market_type), loadExplainSelf(payload.market_type)]);
      toast.success("Symbol scope backend'e kaydedildi");
      return true;
    } catch (error) {
      toast.error(toApiErrorMessage(error, "Symbol scope kaydedilemedi"));
      return false;
    }
  }, [loadExplainSelf, loadScopeSummary, marketType, scopeSummary?.source_mode, scopeSummary?.spot_selected_symbols, scopeSummary?.futures_selected_symbols]);

  const loadSignals = useCallback(async () => {
    const response = await apiClient.get("/user/signals", { params: { limit: 40 }, timeout: 12000 });
    setSignalRows(normalizeSignalRowsForUx(response?.data || []));
  }, []);

  const loadAutomationConfig = useCallback(async () => {
    const response = await apiClient.get("/user/scanner/automation", { timeout: 12000 });
    const payload = response?.data || null;
    setAutomationConfig(payload);
    setAutoMode(payload?.auto_enabled ? "full_auto" : "off");
  }, []);

  const refreshAll = useCallback(async (selectedMarket) => {
    if (!selectedMarket || !["spot", "futures", "both"].includes(String(selectedMarket).toLowerCase())) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      await Promise.all([loadExplainSelf(selectedMarket), loadScopeSummary(selectedMarket), loadSignals(), loadAutomationConfig()]);
    } catch (error) {
      toast.error(toApiErrorMessage(error, "Scanner verisi yüklenemedi"));
    } finally {
      setIsLoading(false);
    }
  }, [loadAutomationConfig, loadExplainSelf, loadScopeSummary, loadSignals]);

  const persistAutomationMode = useCallback(async (nextMode) => {
    if (!hasMarketScope) {
      setAutoMode(nextMode);
      return;
    }
    const normalizedMode = String(nextMode || "off").trim().toLowerCase();
    const selectedSymbols = Array.isArray(scopeDraftSymbols) && scopeDraftSymbols.length > 0
      ? scopeDraftSymbols
      : (Array.isArray(scopeSummary?.selected_symbols) ? scopeSummary.selected_symbols : []);
    const payload = {
      auto_enabled: normalizedMode === "signal" || normalizedMode === "full_auto",
      interval_seconds: Number(automationConfig?.interval_seconds || 60),
      max_results: Number(automationConfig?.max_results || 40),
      symbol_source: String(scopeSummary?.source_mode || "crypto").toLowerCase(),
      symbol_selection_mode: String(scopeSummary?.selection_mode || "manual_selection").toLowerCase(),
      selected_symbols: selectedSymbols,
    };
    try {
      const response = await apiClient.put("/user/scanner/automation", payload, { timeout: 15000 });
      setAutomationConfig(response?.data || payload);
      setAutoMode(normalizedMode);
      toast.success(payload.auto_enabled ? "Scanner automation aktif" : "Scanner automation kapatıldı");
    } catch (error) {
      toast.error(toApiErrorMessage(error, "Automation kaydedilemedi"));
    }
  }, [automationConfig?.interval_seconds, automationConfig?.max_results, hasMarketScope, scopeDraftSymbols, scopeSummary?.selected_symbols, scopeSummary?.selection_mode, scopeSummary?.source_mode]);

  useEffect(() => {
    if (!hasMarketScope) {
      setScopeSummary({});
      setScopeDraftSymbols([]);
      setIsLoading(false);
      return;
    }
    refreshAll(marketType);
  }, [hasMarketScope, marketType, refreshAll]);

  const runPreview = useCallback(async () => {
    if (!hasMarketScope) {
      toast.error("Önce market scope seçin (Spot/Futures/Both)");
      return;
    }
    setIsActionLoading(true);
    try {
      const persistedSymbols = Array.isArray(scopeSummary?.selected_symbols) ? scopeSummary.selected_symbols : [];
      const manualSymbols = persistedSymbols.length > 0
        ? persistedSymbols
        : (marketType === "futures" ? ["BTCUSDT", "ETHUSDT"] : ["BTCUSDT", "ETHUSDT", "SOLUSDT"]);
      const payload = {
        force_refresh: true,
        manual_symbols: manualSymbols,
        manual_symbols_spot: marketType === "futures" ? ["BTCUSDT"] : manualSymbols,
        manual_symbols_futures: marketType === "spot" ? ["BTCUSDT"] : manualSymbols,
      };
      const response = await apiClient.post("/user/scanner-engine/run", payload, { timeout: 20000 });
      setPreviewRows(normalizePreviewRowsForUx(response?.data || {}));
      setLastPreviewAt(new Date().toISOString());
      toast.success("Preview tamamlandı");
      await Promise.all([loadExplainSelf(marketType), loadScopeSummary(marketType)]);
    } catch (error) {
      toast.error(toApiErrorMessage(error, "Preview çalıştırılamadı"));
    } finally {
      setIsActionLoading(false);
    }
  }, [hasMarketScope, loadExplainSelf, loadScopeSummary, marketType, scopeSummary?.selected_symbols]);

  const runScanner = useCallback(async () => {
    if (!hasMarketScope) {
      toast.error("Önce market scope seçin (Spot/Futures/Both)");
      return;
    }
    setIsActionLoading(true);
    try {
      const persistedSymbols = Array.isArray(scopeSummary?.selected_symbols) ? scopeSummary.selected_symbols : [];
      const persistedMode = String(scopeSummary?.selection_mode || "manual_selection").toLowerCase();
      await apiClient.post("/user/scanner/run", {
        mode: "ASSISTED",
        market_type: String(scopeSummary?.market_type || marketType || "spot").toLowerCase(),
        max_results: 40,
        symbol_source: String(scopeSummary?.source_mode || "crypto").toLowerCase(),
        symbol_selection_mode: persistedMode,
        selected_symbols: persistedSymbols,
      }, { timeout: 30000 });
      setLastScannerAt(new Date().toISOString());
      toast.success("Signal run tamamlandı");
      await Promise.all([loadSignals(), loadExplainSelf(marketType), loadScopeSummary(marketType)]);
    } catch (error) {
      toast.error(toApiErrorMessage(error, "Signal run başarısız"));
    } finally {
      setIsActionLoading(false);
    }
  }, [hasMarketScope, loadExplainSelf, loadScopeSummary, loadSignals, marketType, scopeSummary?.market_type, scopeSummary?.selection_mode, scopeSummary?.source_mode, scopeSummary?.selected_symbols]);

  const runFullAuto = useCallback(async () => {
    setIsActionLoading(true);
    try {
      await persistAutomationMode("full_auto");
      await runPreview();
      await runScanner();
    } finally {
      setIsActionLoading(false);
    }
  }, [persistAutomationMode, runPreview, runScanner]);

  const blockReasons = useMemo(() => (Array.isArray(explainSelf?.block_reasons) ? explainSelf.block_reasons : []), [explainSelf]);
  const softSkipReasons = useMemo(() => (Array.isArray(explainSelf?.soft_skip_reasons) ? explainSelf.soft_skip_reasons : []), [explainSelf]);

  const scannerStageStatus = useMemo(() => {
    const stage = scopeSummary?.scanner_stage_status || "waiting";
    const filterStage = scopeSummary?.filter_stage_status || "waiting";
    const signalCreated = Number(scopeSummary?.signal_created_count || signalRows.length || 0) > 0 ? "completed" : "waiting";
    return {
      SCAN: { status: stage, count: Number(scopeSummary?.scanned_count || 0) },
      FILTER: { status: filterStage, count: Number(scopeSummary?.filtered_count || 0) },
      SIGNAL_CREATED: { status: signalCreated, count: Number(scopeSummary?.signal_created_count || signalRows.length || 0) },
    };
  }, [scopeSummary, signalRows.length]);

  return (
    <section className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 md:px-8" data-testid="user-scanner-refactor-page">
      <ScannerStatusHeader
        scannerState={String(scopeSummary?.scanner_state || "idle")}
        market={String(scopeSummary?.market_type || marketType || "Seçilmedi")}
        botStatus={String(explainSelf?.bot_state?.bot_status || "stopped")}
        health={String(explainSelf?.bot_state?.health || "healthy")}
        updatedAt={formatDateTimeShort(explainSelf?.generated_at)}
      />

      <section className="grid gap-4 lg:grid-cols-2" data-testid="user-scanner-auto-mode-section">
        <article className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-scanner-auto-mode-card">
          <h2 className="mb-3 text-base font-semibold text-slate-900" data-testid="user-scanner-auto-mode-title">Auto Mode</h2>
          <div className="grid gap-2 sm:grid-cols-2" data-testid="user-scanner-auto-mode-options">
            {UX_AUTO_MODES.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => persistAutomationMode(option.value)}
                className={`rounded-xl border p-3 text-left transition ${autoMode === option.value ? "border-cyan-500 bg-cyan-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
                data-testid={`user-scanner-auto-mode-option-${option.value}`}
              >
                <p className="text-sm font-semibold text-slate-900" data-testid={`user-scanner-auto-mode-option-label-${option.value}`}>{option.label}</p>
                <p className="text-xs text-slate-600" data-testid={`user-scanner-auto-mode-option-description-${option.value}`}>{option.description}</p>
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="user-scanner-auto-mode-selected-text">Seçili mod: {activeAutoMode.label}</p>
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-scanner-market-action-card">
          <h2 className="mb-3 text-base font-semibold text-slate-900" data-testid="user-scanner-market-selection-title">Market + Symbol Scope</h2>
          <div className="mb-4 flex gap-2" data-testid="user-scanner-market-selection-options">
            {UX_MARKETS.map((market) => (
              <button
                key={market.value}
                type="button"
                onClick={() => setMarketType(market.value)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${marketType === market.value ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-700"}`}
                data-testid={`user-scanner-market-option-${market.value}`}
              >
                {market.label}
              </button>
            ))}
          </div>
          {!hasMarketScope && <p className="mb-3 text-xs text-amber-600" data-testid="user-scanner-market-required-text">Önce Spot, Futures veya Both market seçin.</p>}

          <div className="flex flex-wrap gap-2" data-testid="user-scanner-action-buttons">
            <Button type="button" variant="outline" onClick={() => refreshAll(marketType)} disabled={isActionLoading || !hasMarketScope} data-testid="user-scanner-action-refresh-button">Yenile</Button>
            <Button type="button" onClick={runPreview} disabled={isActionLoading || !hasMarketScope} data-testid="user-scanner-action-preview-button">Önizleme Çalıştır</Button>
            <Button type="button" variant="secondary" onClick={runScanner} disabled={isActionLoading || !hasMarketScope} data-testid="user-scanner-action-scanner-button">Signal Çalıştır</Button>
            <Button type="button" variant="default" onClick={runFullAuto} disabled={isActionLoading || autoMode !== "full_auto" || !hasMarketScope} data-testid="user-scanner-action-full-auto-button">Full Auto</Button>
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="user-scanner-action-preview-hint">Önizleme: sinyal yazmaz · Signal: scanner pipeline başlatır</p>
        </article>
      </section>

      <ActiveSymbolScopeCard
        scope={scopeSummary}
        onEditScope={() => setScopeEditorOpen((prev) => !prev)}
        onViewAll={() => setResultsTab("signals")}
        onChangeList={() => setScopeEditorOpen(true)}
      />

      {scopeEditorOpen && (
        <div className="rounded-2xl border border-slate-200 bg-slate-950 p-4" data-testid="user-scanner-scope-editor-panel">
          <SymbolSelectorPanel
            testIdPrefix="user-scanner-scope-selector"
            source={String(scopeSummary?.source_mode || "crypto")}
            mode={String(scopeSummary?.selection_mode || "manual_selection")}
            marketType={marketType}
            onMarketTypeChange={setMarketType}
            selectedSymbols={scopeDraftSymbols}
            selectionSummary={{
              selected_list_name: scopeSummary?.selected_list_name,
              last_updated: scopeSummary?.updated_at,
            }}
            onSelectedSymbolsChange={setScopeDraftSymbols}
            onWatchlistApplied={setScopeDraftSymbols}
            onPersistSelection={persistSymbolSelection}
          />
        </div>
      )}

      <UserPipelineStatusBarV2
        mode="scanner"
        stages={scannerStageStatus}
        summary={{
          scanned_count: Number(scopeSummary?.scanned_count || 0),
          filtered_count: Number(scopeSummary?.filtered_count || 0),
          signal_created_count: Number(scopeSummary?.signal_created_count || signalRows.length || 0),
        }}
      />

      {isLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-scanner-explain-panel-loading"><LoadingSkeleton className="h-24 w-full" /></div>
      ) : (
        <WhyNoSignalCard
          scannerState={String(scopeSummary?.scanner_state || "idle")}
          selectionEmpty={Boolean(scopeSummary?.selection_empty)}
          selectedSymbolCount={Number(scopeSummary?.selected_symbol_count || 0)}
          hardBlocks={blockReasons}
          softSkips={softSkipReasons}
          hasSignals={signalRows.length > 0}
          onGoSignals={() => navigate("/user/signals")}
          onEditScope={() => setScopeEditorOpen(true)}
          onRelaxFilters={() => toast.info("Filtre gevşetme önerisi: volume/spread threshold düşür.")}
        />
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-scanner-results-section">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2" data-testid="user-scanner-results-header">
          <h2 className="text-base font-semibold text-slate-900" data-testid="user-scanner-results-title">Recent Scanner Results</h2>
          <div className="flex gap-2" data-testid="user-scanner-results-tabs">
            <Button type="button" variant={resultsTab === "signals" ? "default" : "outline"} onClick={() => setResultsTab("signals")} data-testid="user-scanner-results-tab-signals-button">
              Signal ({signalRows.length})
            </Button>
            <Button type="button" variant={resultsTab === "preview" ? "default" : "outline"} onClick={() => setResultsTab("preview")} data-testid="user-scanner-results-tab-preview-button">
              Preview ({previewRows.length})
            </Button>
            {signalRows.length > 0 && (
              <Button type="button" variant="outline" onClick={() => navigate("/user/signals")} data-testid="user-scanner-go-signals-button">Sinyalleri Gör</Button>
            )}
            <Button type="button" variant="outline" onClick={() => navigate("/user/signal-flow")} data-testid="user-scanner-go-signal-flow-button">Signal Flow</Button>
          </div>
        </div>

        <div className="mb-4" data-testid="user-scanner-v5-bot-trader-wrap">
          <V5BotTraderPanel limit={50} refreshSignal={signalRows.length} />
        </div>

        {resultsTab === "signals" ? (
          <div className="overflow-x-auto" data-testid="user-scanner-results-signals-table-wrap">
            <table className="min-w-full text-sm" data-testid="user-scanner-results-signals-table">
              <thead data-testid="user-scanner-results-signals-table-head">
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500" data-testid="user-scanner-results-signals-head-row">
                  <th className="px-2 py-2" data-testid="user-scanner-results-signals-head-tier">Tier</th>
                  <th className="px-2 py-2" data-testid="user-scanner-results-signals-head-symbol">Symbol</th>
                  <th className="px-2 py-2" data-testid="user-scanner-results-signals-head-signal">Signal</th>
                  <th className="px-2 py-2 text-right" data-testid="user-scanner-results-signals-head-ranking">Rank</th>
                  <th className="px-2 py-2 text-right" data-testid="user-scanner-results-signals-head-quality">Quality</th>
                  <th className="px-2 py-2 text-right" data-testid="user-scanner-results-signals-head-volume">24h Vol</th>
                  <th className="px-2 py-2 text-right" data-testid="user-scanner-results-signals-head-cmf">CMF</th>
                  <th className="px-2 py-2 text-right" data-testid="user-scanner-results-signals-head-confidence">Conf</th>
                  <th className="px-2 py-2" data-testid="user-scanner-results-signals-head-status">Status</th>
                  <th className="px-2 py-2" data-testid="user-scanner-results-signals-head-time">Time</th>
                </tr>
              </thead>
              <tbody data-testid="user-scanner-results-signals-table-body">
                {signalRows.length === 0 ? (
                  <tr data-testid="user-scanner-results-signals-empty-row">
                    <td colSpan={10} className="px-2 py-3 text-xs text-slate-500" data-testid="user-scanner-results-signals-empty-text">Signal sonucu yok.</td>
                  </tr>
                ) : signalRows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100" data-testid={`user-scanner-results-signals-row-${row.id}`}>
                    <td className="px-2 py-2" data-testid={`user-scanner-results-signals-tier-${row.id}`}>
                      {row.tier && row.tier !== "-" ? (
                        <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${TIER_BADGE_COLORS[row.tier] || TIER_BADGE_COLORS.C}`}>{row.tier}</span>
                      ) : "-"}
                    </td>
                    <td className="px-2 py-2 font-mono" data-testid={`user-scanner-results-signals-symbol-${row.id}`}>{row.symbol}</td>
                    <td className="px-2 py-2" data-testid={`user-scanner-results-signals-signal-${row.id}`}>
                      <span className={row.signal === "LONG" ? "text-emerald-700 font-semibold" : row.signal === "SHORT" ? "text-red-700 font-semibold" : ""}>{row.signal}</span>
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs" data-testid={`user-scanner-results-signals-ranking-${row.id}`}>
                      {row.ranking_score !== null ? row.ranking_score.toFixed(2) : "-"}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs" data-testid={`user-scanner-results-signals-quality-${row.id}`}>
                      {row.quality_score !== null ? row.quality_score.toFixed(0) : "-"}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs" data-testid={`user-scanner-results-signals-volume-${row.id}`}>
                      {formatShortNumber(row.volume_24h)}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs" data-testid={`user-scanner-results-signals-cmf-${row.id}`}>
                      {row.cmf !== null ? (
                        <span className={row.cmf > 0 ? "text-emerald-700" : row.cmf < 0 ? "text-red-700" : ""}>{row.cmf.toFixed(2)}</span>
                      ) : "-"}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs" data-testid={`user-scanner-results-signals-confidence-${row.id}`}>
                      {row.confidence !== null ? `${(row.confidence * 100).toFixed(0)}%` : "-"}
                    </td>
                    <td className="px-2 py-2 text-xs" data-testid={`user-scanner-results-signals-status-${row.id}`}>{row.status}</td>
                    <td className="px-2 py-2 text-xs" data-testid={`user-scanner-results-signals-time-${row.id}`}>{formatDateTimeShort(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="user-scanner-results-preview-table-wrap">
            <table className="min-w-full text-sm" data-testid="user-scanner-results-preview-table">
              <thead data-testid="user-scanner-results-preview-table-head">
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500" data-testid="user-scanner-results-preview-head-row">
                  <th className="px-2 py-2" data-testid="user-scanner-results-preview-head-symbol">Symbol</th>
                  <th className="px-2 py-2" data-testid="user-scanner-results-preview-head-score">Score</th>
                  <th className="px-2 py-2" data-testid="user-scanner-results-preview-head-tier">Tier</th>
                  <th className="px-2 py-2" data-testid="user-scanner-results-preview-head-market">Market</th>
                </tr>
              </thead>
              <tbody data-testid="user-scanner-results-preview-table-body">
                {previewRows.length === 0 ? (
                  <tr data-testid="user-scanner-results-preview-empty-row">
                    <td colSpan={4} className="px-2 py-3 text-xs text-slate-500" data-testid="user-scanner-results-preview-empty-text">Preview sonucu yok.</td>
                  </tr>
                ) : previewRows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100" data-testid={`user-scanner-results-preview-row-${row.id}`}>
                    <td className="px-2 py-2 font-mono" data-testid={`user-scanner-results-preview-symbol-${row.id}`}>{row.symbol}</td>
                    <td className="px-2 py-2" data-testid={`user-scanner-results-preview-score-${row.id}`}>{row.score.toFixed(2)}</td>
                    <td className="px-2 py-2" data-testid={`user-scanner-results-preview-tier-${row.id}`}>{row.tier}</td>
                    <td className="px-2 py-2" data-testid={`user-scanner-results-preview-market-${row.id}`}>{row.market_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 grid gap-2 text-xs text-slate-500 sm:grid-cols-3" data-testid="user-scanner-results-footer-info">
          <p data-testid="user-scanner-results-footer-last-preview">Son Preview: {formatDateTimeShort(lastPreviewAt)}</p>
          <p data-testid="user-scanner-results-footer-last-scanner">Son Scanner: {formatDateTimeShort(lastScannerAt)}</p>
          <p data-testid="user-scanner-results-footer-auto-mode">Auto Mode: {activeAutoMode.label}</p>
        </div>
      </section>
    </section>
  );
};